import { Fragment, useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, UserX } from "lucide-react";
import { useConversationDeepLink } from "@/hooks/useConversationDeepLink";

// Shared column template for the wide (browse) employee list: header row and
// every person row must use the same tracks so columns line up.
// Columns: Pracownik | Płaca | Rola | E-mail | Uwagi
const EMPLOYEE_LIST_GRID =
  "md:grid-cols-[minmax(13rem,15rem)_8rem_7.5rem_minmax(10rem,16rem)_minmax(0,1fr)]";

type EmployeeSortCol = "name" | "salary" | "role" | "email";

// Hierarchy used when sorting by the role column (CEO handled separately).
const ROLE_SORT_ORDER: Record<string, number> = {
  director: 1,
  coordinator: 2,
  accountant: 3,
  hr: 4,
  employee: 5,
};
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate, formatDateTime } from "@/utils/formatDate";
import { useAuthStore } from "@/store/auth";
import { useCompanies } from "@/features/group_finance/hooks/useGroupFinance";
import { getRoleLabel, getRoleBadgeColor } from "@/utils/roleLabel";
import { type EmployeeSalaryCost } from "@/features/finances/types";
import {
  LEAVE_TYPE_LABELS,
  LEAVE_STATUS_COLORS,
  SICK_LEAVE_STATUS_COLORS,
} from "@/features/leaves/types";
import { sickLeavesApi } from "@/features/leaves/api/leaves";
import {
  useSalaryCosts,
  useCreateSalaryCost,
  useDeleteSalaryCost,
  useUpdateSalaryCost,
  useSetSalaryCostPrimary,
  useEmployeeAllocations,
  useCreateEmployeeAllocation,
  useDeleteEmployeeAllocation,
  usePropDistExclusions,
  useCreatePropDistExclusion,
  useDeletePropDistExclusion,
} from "@/features/finances/hooks/useFinances";
import { propDistExclusionsApi, employeeAllocationsApi } from "@/features/finances/api/finances";
import { Modal } from "@/components/ui/Modal";
import {
  SheetCalcProvider,
  SheetCalcToggleButton,
  CalcCell,
  useSheetCalcActive,
} from "@/features/finances/components/SheetCalc";
import { EmployeeCommentThread } from "@/features/finances/components/EmployeeCommentThread";
import { PageLoader } from "@/components/ui/Spinner";
import { RemoteWorkResolvedTable } from "@/features/schedule/components/RemoteWorkResolvedTable";
import { EmployeeExaminationPanel } from "@/features/finances/components/EmployeeExaminationPanel";
import { EmployeeDocumentsPanel } from "@/features/finances/components/EmployeeDocumentsPanel";
import { PrimaryEmploymentContractsPanel } from "@/features/finances/components/PrimaryEmploymentContractsPanel";
import { SecondaryEmploymentContractsPanel } from "@/features/finances/components/SecondaryEmploymentContractsPanel";
import { EmployeeESignaturePanel } from "@/features/finances/components/EmployeeESignaturePanel";
import { EmployeeDaysOffPanel } from "@/features/schedule/components/EmployeeDaysOffPanel";
import { useOwedDaysOff } from "@/features/schedule/hooks/useSchedule";
import { downloadESignaturesXlsx } from "@/features/finances/api/employeeDocs";
import { DocumentTypeManagerPage } from "@/features/finances/pages/DocumentTypeManagerPage";
import type { ExaminationStatusDetail } from "@/features/finances/types.employee_docs";
import { getNthWorkingDay } from "@/utils/workingDays";
import { timesheetsApi } from "@/features/timesheets/api/timesheets";
import { useMissingDismissals } from "@/features/timesheets/hooks/useTimesheets";
import { MONTH_NAMES } from "@/features/timesheets/types";

interface ContractExpiryInfo {
  company_name: string;
  effective_to: string;
  days_to_expiry: number;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  is_active: boolean;
  is_ceo: boolean;
  is_uz: boolean;
  contract_type: string;
  employment_fraction_numerator: number;
  employment_fraction_denominator: number;
  work_type: "on_site" | "remote";
  contract_expiry_info: ContractExpiryInfo[];
  /** Derived by the backend from salary costs (primary → oldest); read-only.
   *  null = no costs = considered already working. */
  employment_start_date: string | null;
  planned_dismissal_date: string | null;
  scheduled_deactivation_at: string | null;
  profile_picture_url?: string | null;
  job_title?: string;
  /** Optional office entrance (access) card number — free text, HR-entered. */
  office_entry_card_number?: string;
  branch?: { id: string; name: string; city?: string } | null;
  bhp_status?: ExaminationStatusDetail | null;
  medical_status?: ExaminationStatusDetail | null;
  health_todo?: boolean | null;
  contract_docs_status?: ExaminationStatusDetail | null;
  esignature_status?: ExaminationStatusDetail | null;
  has_salary_view_access?: boolean;
  contract_scan_info?: {
    id: string;
    url: string;
    original_filename: string;
    size_bytes: number | null;
    uploaded_at: string | null;
  } | null;
}

interface LeaveEntitlement {
  id: string;
  year: number;
  days_total: number;
  prev_year_carryover: number;
  employment_fraction_numerator: number;
  employment_fraction_denominator: number;
  used_days: number;
  used_hours_remainder: number;
  used_minutes_remainder: number;
  available_days: number;
  available_hours_remainder: number;
  available_minutes_remainder: number;
}

function fmtEntitlementBalance(days: number, hours: number, minutes: number): string {
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}min`);
  return parts.length > 0 ? parts.join(" ") : "0d";
}

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  uop: "UoP",
  uz: "UZ",
  b2b: "B2B",
};

/** Today's local date as YYYY-MM-DD (toISOString would shift it to UTC). */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface Project {
  id: string;
  name: string;
  short_name: string;
  status: string;
  total_value: string | null;
  client_name?: string | null;
}

const TILE_BORDER_CLASSES = [
  "border-blue-400 text-blue-700 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400 dark:text-blue-300",
  "border-emerald-400 text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:border-emerald-400 dark:text-emerald-300",
  "border-violet-400 text-violet-700 bg-violet-50 dark:bg-violet-900/30 dark:border-violet-400 dark:text-violet-300",
  "border-amber-400 text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-400 dark:text-amber-300",
  "border-rose-400 text-rose-700 bg-rose-50 dark:bg-rose-900/30 dark:border-rose-400 dark:text-rose-300",
  "border-cyan-400 text-cyan-700 bg-cyan-50 dark:bg-cyan-900/30 dark:border-cyan-400 dark:text-cyan-300",
  "border-orange-400 text-orange-700 bg-orange-50 dark:bg-orange-900/30 dark:border-orange-400 dark:text-orange-300",
  "border-teal-400 text-teal-700 bg-teal-50 dark:bg-teal-900/30 dark:border-teal-400 dark:text-teal-300",
  "border-fuchsia-400 text-fuchsia-700 bg-fuchsia-50 dark:bg-fuchsia-900/30 dark:border-fuchsia-400 dark:text-fuchsia-300",
  "border-lime-400 text-lime-700 bg-lime-50 dark:bg-lime-900/30 dark:border-lime-400 dark:text-lime-300",
];
const TILE_DOT_CLASSES = [
  "bg-blue-400", "bg-emerald-400", "bg-violet-400", "bg-amber-400", "bg-rose-400",
  "bg-cyan-400", "bg-orange-400", "bg-teal-400", "bg-fuchsia-400", "bg-lime-400",
];
const TILE_TEXT_CLASSES = [
  "text-blue-600 dark:text-blue-400", "text-emerald-600 dark:text-emerald-400",
  "text-violet-600 dark:text-violet-400", "text-amber-600 dark:text-amber-400",
  "text-rose-600 dark:text-rose-400", "text-cyan-600 dark:text-cyan-400",
  "text-orange-600 dark:text-orange-400", "text-teal-600 dark:text-teal-400",
  "text-fuchsia-600 dark:text-fuchsia-400", "text-lime-600 dark:text-lime-400",
];

const MONTHS = [
  "Sty",
  "Lut",
  "Mar",
  "Kwi",
  "Maj",
  "Cze",
  "Lip",
  "Sie",
  "Wrz",
  "Paź",
  "Lis",
  "Gru",
];

const SCHEDULE_TYPE_LABELS: Record<string, string> = {
  remote: "Praca zdalna",
  uz_present: "Obecność UZ",
};

const SCHEDULE_STATUS_COLORS: Record<
  string,
  "yellow" | "green" | "red" | "gray" | "blue"
> = {
  pending: "yellow",
  approved: "green",
  rejected: "red",
  confirmed: "blue",
};

function addMonths(d: Date, n: number): Date {
  const r = new Date(d);
  r.setMonth(r.getMonth() + n);
  return r;
}

// Per-user key (mirrors Faktury PRZ): plain logout does NOT clear localStorage,
// so a shared-computer user must never see the previous user's amounts.
const employeesSheetCalcKey = (userId: string | undefined) =>
  `elg5.employees.sheetCalc.v1.${userId ?? "anon"}`;

export function EmployeesPage() {
  const { user } = useAuthStore();
  return (
    <SheetCalcProvider storageKey={employeesSheetCalcKey(user?.id)}>
      <EmployeesPageInner />
    </SheetCalcProvider>
  );
}

function EmployeesPageInner() {
  const qc = useQueryClient();
  const { user: authUser } = useAuthStore();
  const isDirector = authUser?.role === "director";
  const isCEO = !!authUser?.is_ceo;
  const canManageUsers = isDirector || isCEO;
  // Individual days off in lieu of a holiday — same gate as company days off
  // (backend IsTechnicalDirector: CEO or technical director).
  const canManageDaysOff = isCEO || (isDirector && !!authUser?.is_technical_director);
  const { data: owedDaysOff } = useOwedDaysOff();
  const owedDaysOffByEmployee = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const o of owedDaysOff ?? []) {
      map.set(o.employee, [...(map.get(o.employee) ?? []), o.compensates_for]);
    }
    return map;
  }, [owedDaysOff]);
  const canSeeAnalytics =
    isDirector || authUser?.role === "accountant" || authUser?.role === "hr";
  // "Licz jako arkusz" mode — clicking amounts must not open the detail panel.
  const calcActive = useSheetCalcActive();

  const [analyticsExpanded, setAnalyticsExpanded] = useState(false);
  const [analyticsBusy, setAnalyticsBusy] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  async function handleExportESignatures() {
    setAnalyticsBusy(true);
    setAnalyticsError(null);
    try {
      await downloadESignaturesXlsx();
    } catch {
      setAnalyticsError("Nie udało się pobrać pliku.");
    } finally {
      setAnalyticsBusy(false);
    }
  }

  // Salary-cost XLSX export (technical director / CEO). The backend endpoint
  // reuses the salary-costs queryset scoping (directors: explicit
  // SalaryViewAccess grants only) — the file can never contain more than the
  // table on this page shows.
  const canExportSalaryCosts =
    isCEO || (isDirector && !!authUser?.is_technical_director);
  const [salaryExportBusy, setSalaryExportBusy] = useState(false);

  async function handleExportSalaryCosts() {
    setSalaryExportBusy(true);
    setAnalyticsError(null);
    try {
      const resp = await apiClient.get<Blob>("/finances/salary-costs/export/", {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `koszty_plac_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setAnalyticsError("Nie udało się pobrać pliku.");
    } finally {
      setSalaryExportBusy(false);
    }
  }
  const today = new Date();

  const currentMonthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
  const lastDayNum = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0,
  ).getDate();
  const currentMonthEnd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(lastDayNum).padStart(2, "0")}`;

  // ── Missing UZ timesheet detection ────────────────────────────────────────
  const prevMonth = today.getMonth() === 0 ? 12 : today.getMonth();
  const prevYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
  const TIMESHEET_CUTOFF = { year: 2026, month: 4 };
  const prevMonthBeforeCutoff =
    prevYear < TIMESHEET_CUTOFF.year ||
    (prevYear === TIMESHEET_CUTOFF.year && prevMonth <= TIMESHEET_CUTOFF.month);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const fifthWorkingDay = getNthWorkingDay(today.getFullYear(), today.getMonth() + 1, 5);
  const pastDeadline = fifthWorkingDay !== null && todayStr >= fifthWorkingDay;
  const canSeeAllTimesheets = isDirector || authUser?.role === "accountant";
  const checkMissingTimesheets = canSeeAllTimesheets && !prevMonthBeforeCutoff && pastDeadline;

  // ── Data fetching ──────────────────────────────────────────────────────────

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () =>
      apiClient.get<{ data: User[] }>("/auth/users/").then((r) => r.data.data),
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () =>
      apiClient.get<{ data: Project[] }>("/projects/").then((r) => r.data.data),
  });

  // NOTE: `review: "1"` is required — without it the list endpoint is self-scoped
  // (returns only the caller's own timesheets), so every other UZ employee would
  // wrongly appear as "missing". With it, the list is branch-scoped for reviewers.
  // The distinct query key also avoids colliding with the self-scoped
  // `useMissingTimesheet` query, which shares the same period and relies on self-scoping.
  const { data: prevMonthTimesheets = [] } = useQuery({
    queryKey: ["timesheets", { year: String(prevYear), month: String(prevMonth), review: "1" }],
    queryFn: () =>
      timesheetsApi.list({ year: String(prevYear), month: String(prevMonth), review: "1" }),
    staleTime: 1000 * 60 * 5,
    enabled: checkMissingTimesheets,
  });
  const submittedEmployeeIds = new Set(prevMonthTimesheets.map((t) => t.employee));
  const { dismissedIds: dismissedTimesheetAlertIds, recentDismissalIds: recentTimesheetDismissalIds, dismiss: dismissTimesheetAlert, undismiss: undismissTimesheetAlert } =
    useMissingDismissals(prevYear, prevMonth, checkMissingTimesheets);
  // Employment started after the previous month ended — no timesheet expected,
  // so the "Brak karty pracy" hints are suppressed for that employee.
  // ISO-string comparison avoids UTC-vs-local parsing pitfalls;
  // new Date(prevYear, prevMonth, 0).getDate() = number of days in the 1-based prevMonth.
  const prevMonthEndStr = `${prevYear}-${String(prevMonth).padStart(2, "0")}-${String(new Date(prevYear, prevMonth, 0).getDate()).padStart(2, "0")}`;
  const notYetEmployedInPrevMonth = (u: { employment_start_date: string | null }) =>
    !!u.employment_start_date && u.employment_start_date > prevMonthEndStr;

  const { data: salaryCosts = [] } = useSalaryCosts();
  const createSalary = useCreateSalaryCost();
  const deleteSalary = useDeleteSalaryCost();
  const updateSalary = useUpdateSalaryCost();
  const setPrimary = useSetSalaryCostPrimary();
  const [editingCompanyFor, setEditingCompanyFor] = useState<string | null>(
    null,
  );

  const ownBranchId = authUser?.branch?.id;

  // ── Column sorting (branch grouping always takes precedence) ──────────────
  const [sortCol, setSortCol] = useState<EmployeeSortCol>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const toggleSort = (col: EmployeeSortCol) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir(col === "salary" ? "desc" : "asc");
    }
  };

  // Current total monthly salary per employee — needed to sort by the salary
  // column (mirrors the per-row activeScs computation further down).
  const activeMonthlyByUser = new Map<string, number>();
  for (const s of salaryCosts) {
    const effTo =
      s.effective_to && s.effective_to < "2099-01-01"
        ? s.effective_to
        : "2099-12-31";
    if (s.effective_from <= todayStr && effTo >= todayStr) {
      activeMonthlyByUser.set(
        s.employee,
        (activeMonthlyByUser.get(s.employee) ?? 0) +
          parseFloat(s.monthly_cost),
      );
    }
  }

  const roleRank = (u: User) => (u.is_ceo ? 0 : ROLE_SORT_ORDER[u.role] ?? 9);
  const fullName = (u: User) => `${u.first_name} ${u.last_name}`;
  const sortColCmp = (a: User, b: User): number => {
    switch (sortCol) {
      case "salary":
        return (
          (activeMonthlyByUser.get(a.id) ?? -1) -
          (activeMonthlyByUser.get(b.id) ?? -1)
        );
      case "role":
        return roleRank(a) - roleRank(b);
      case "email":
        return (a.email ?? "").localeCompare(b.email ?? "", "pl");
      default:
        return fullName(a).localeCompare(fullName(b), "pl");
    }
  };

  const internalUsers = users
    .filter((u) => u.role !== "contractor")
    .sort((a, b) => {
      // Blocked accounts always sink to the very bottom of the list.
      const aActive = a.is_active ? 0 : 1;
      const bActive = b.is_active ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      // Branch grouping is superior to the column sort; the blocked section at
      // the bottom is flat, so it sorts by column only.
      if (a.is_active) {
        const aIsOwn = a.branch?.id === ownBranchId ? 0 : 1;
        const bIsOwn = b.branch?.id === ownBranchId ? 0 : 1;
        if (aIsOwn !== bIsOwn) return aIsOwn - bIsOwn;
        const branchCmp = (a.branch?.name ?? "").localeCompare(
          b.branch?.name ?? "",
          "pl",
        );
        if (branchCmp !== 0) return branchCmp;
      }
      const colCmp = sortColCmp(a, b);
      if (colCmp !== 0) return (sortDir === "asc" ? 1 : -1) * colCmp;
      return fullName(a).localeCompare(fullName(b), "pl");
    });
  const showBranchHeaders = isCEO && internalUsers.some((u) => u.branch);
  const activeProjects = projects.filter(
    (p) => p.status === "active" || p.status === "suspended",
  );

  // ── Selection ──────────────────────────────────────────────────────────────

  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const selectedEmp =
    internalUsers.find((u) => u.id === selectedEmployee) ?? null;

  // Contract scan upload/replace: CEO, accountant and HR always; a non-CEO
  // director additionally needs the SalaryViewAccess grant (mirrors backend).
  const canManageContractScans =
    isCEO ||
    authUser?.role === "accountant" ||
    authUser?.role === "hr" ||
    (isDirector && !!selectedEmp?.has_salary_view_access);

  const detailPanelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    detailPanelRef.current?.scrollTo({ top: 0 });
  }, [selectedEmployee]);

  // Deep link z powiadomienia o oznaczeniu w notatkach pracownika:
  // ?employee=<uuid>&tab=conversation wybiera tę osobę i przewija do jej wątku.
  const [employeeParams] = useSearchParams();
  const deepEmployeeId = employeeParams.get("employee");
  useEffect(() => {
    if (deepEmployeeId) setSelectedEmployee(deepEmployeeId);
  }, [deepEmployeeId]);
  const { ref: commentsRef } = useConversationDeepLink<HTMLDivElement>(
    !!deepEmployeeId && selectedEmployee === deepEmployeeId,
  );

  // Allocation month range: today .. today+3, capped by dismissal date
  const allocationMonths = Array.from({ length: 4 }, (_, i) =>
    addMonths(today, i),
  ).filter((d) => {
    if (!selectedEmp?.planned_dismissal_date) return true;
    const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
    return monthStart <= new Date(selectedEmp.planned_dismissal_date);
  });

  const [allocYear, setAllocYear] = useState(today.getFullYear());
  const [allocMonth, setAllocMonth] = useState(today.getMonth() + 1);
  const [propagateToFuture, setPropagateToFuture] = useState(false);

  const { data: allocations = [] } = useEmployeeAllocations({
    employee: selectedEmployee ?? undefined,
    year: allocYear,
    month: allocMonth,
  });
  const createAlloc = useCreateEmployeeAllocation();
  const deleteAlloc = useDeleteEmployeeAllocation();

  const maxDate = addMonths(today, 3);
  const isMonthInRange = (y: number, m: number) => {
    if (y < today.getFullYear()) return false;
    if (y > maxDate.getFullYear()) return false;
    if (y === today.getFullYear() && m < today.getMonth() + 1) return false;
    if (y === maxDate.getFullYear() && m > maxDate.getMonth() + 1) return false;
    return true;
  };

  // ── New employee form ──────────────────────────────────────────────────────

  const [showDocTypes, setShowDocTypes] = useState(false);
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    password: "",
    password2: "",
    role: "employee",
    branch: authUser?.branch?.id ?? "",
  });
  const [newUserErr, setNewUserErr] = useState<string | null>(null);

  const createUserMutation = useMutation({
    mutationFn: (data: typeof newUserForm) =>
      apiClient
        .post<{ data: User }>("/auth/users/", { ...data, username: data.email, branch: data.branch || null })
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setNewUserForm({
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        password: "",
        password2: "",
        role: "employee",
        branch: "",
      });
      setShowNewUser(false);
      setNewUserErr(null);
    },
    onError: (err: unknown) => {
      // Surface the backend's field errors — the generic message hid the real
      // cause (e.g. "email: Podaj poprawny adres e-mail.").
      const FIELD_LABELS: Record<string, string> = {
        username: "E-mail (login)",
        email: "E-mail",
        first_name: "Imię",
        last_name: "Nazwisko",
        phone_number: "Telefon",
        password: "Hasło",
        role: "Rola",
        branch: "Oddział",
      };
      const errors = (err as { response?: { data?: { errors?: Record<string, unknown> } } })
        ?.response?.data?.errors;
      const parts = errors
        ? Object.entries(errors).map(([field, msg]) => {
            const label = FIELD_LABELS[field] ?? field;
            const text = Array.isArray(msg) ? msg.join(" ") : String(msg);
            return `${label}: ${text}`;
          })
        : [];
      setNewUserErr(
        parts.length > 0 ? parts.join(" · ") : "Błąd tworzenia konta. Sprawdź dane.",
      );
    },
  });

  // ── Dismissal date ─────────────────────────────────────────────────────────

  const [editDismissalFor, setEditDismissalFor] = useState<string | null>(null);
  const [dismissalInput, setDismissalInput] = useState("");

  const setDismissalMutation = useMutation({
    mutationFn: ({ id, date }: { id: string; date: string | null }) =>
      apiClient
        .patch<{
          data: User;
        }>(`/auth/users/${id}/`, { planned_dismissal_date: date })
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setEditDismissalFor(null);
    },
  });

  // ── Branch list ───────────────────────────────────────────────────────────
  const { data: branches = [] } = useQuery({
    queryKey: ["branches"],
    queryFn: () =>
      apiClient.get<{ data: { id: string; name: string }[] }>("/auth/branches/").then((r) => r.data.data),
    staleTime: 0,
  });

  const setBranchMutation = useMutation({
    mutationFn: ({ id, branch }: { id: string; branch: string | null }) =>
      apiClient.patch<{ data: User }>(`/auth/users/${id}/`, { branch }).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  // ── Salary cost form ───────────────────────────────────────────────────────

  const { data: companies = [] } = useCompanies(
    isCEO || authUser?.role === "accountant" || !!authUser?.has_group_finance_access,
  );

  const [showSalaryForm, setShowSalaryForm] = useState<string | null>(null);
  const [salaryForm, setSalaryForm] = useState({
    monthly_cost: "",
    effective_from: "",
    effective_to: "",
    company: "",
  });
  const [salaryHasEndDate, setSalaryHasEndDate] = useState(false);
  const [salaryErr, setSalaryErr] = useState<string | null>(null);

  const [editingSalaryId, setEditingSalaryId] = useState<string | null>(null);
  const [editSalaryForm, setEditSalaryForm] = useState({
    monthly_cost: "",
    effective_from: "",
    effective_to: "",
    company: "",
  });
  const [editSalaryHasEndDate, setEditSalaryHasEndDate] = useState(false);
  const [editSalaryErr, setEditSalaryErr] = useState<string | null>(null);

  const handleEditSalaryOpen = (s: EmployeeSalaryCost) => {
    const hasEnd = !!s.effective_to && s.effective_to < "2099-01-01";
    setEditingSalaryId(s.id);
    setEditSalaryForm({
      monthly_cost: s.monthly_cost,
      effective_from: s.effective_from,
      effective_to: hasEnd ? s.effective_to! : "",
      company: s.company ?? "",
    });
    setEditSalaryHasEndDate(hasEnd);
    setEditSalaryErr(null);
  };

  const handleUpdateSalary = async () => {
    if (!editingSalaryId) return;
    try {
      const patch: Record<string, unknown> = {};
      if (isCEO) {
        if (!editSalaryForm.monthly_cost || !editSalaryForm.effective_from) {
          setEditSalaryErr("Kwota i data od są wymagane.");
          return;
        }
        patch.monthly_cost = editSalaryForm.monthly_cost;
        patch.effective_from = editSalaryForm.effective_from;
        patch.company = editSalaryForm.company || null;
      }
      patch.effective_to = editSalaryHasEndDate
        ? editSalaryForm.effective_to || "2099-12-31"
        : "2099-12-31";
      await updateSalary.mutateAsync({ id: editingSalaryId, data: patch });
      setEditingSalaryId(null);
      setEditSalaryErr(null);
    } catch {
      setEditSalaryErr("Błąd zapisu.");
    }
  };

  const handleCreateSalary = async (employeeId: string) => {
    if (!salaryForm.monthly_cost || !salaryForm.effective_from) {
      setSalaryErr("Kwota i data od są wymagane.");
      return;
    }
    try {
      await createSalary.mutateAsync({
        employee: employeeId,
        monthly_cost: salaryForm.monthly_cost,
        effective_from: salaryForm.effective_from,
        effective_to: salaryHasEndDate
          ? salaryForm.effective_to || "2099-12-31"
          : "2099-12-31",
        company: salaryForm.company || null,
      });
      setSalaryForm({
        monthly_cost: "",
        effective_from: "",
        effective_to: "",
        company: "",
      });
      setSalaryHasEndDate(false);
      setShowSalaryForm(null);
      setSalaryErr(null);
    } catch {
      setSalaryErr("Błąd zapisu.");
    }
  };

  // ── Block / scheduled block ────────────────────────────────────────────────

  const [showScheduleBlock, setShowScheduleBlock] = useState(false);
  const [scheduleBlockInput, setScheduleBlockInput] = useState("");
  const [blockErr, setBlockErr] = useState<string | null>(null);

  const blockUserMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiClient
        .patch<{ data: User }>(`/auth/users/${id}/`, { is_active: active })
        .then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const scheduleBlockMutation = useMutation({
    mutationFn: ({ id, at }: { id: string; at: string | null }) =>
      apiClient
        .patch<{
          data: User;
        }>(`/auth/users/${id}/`, { scheduled_deactivation_at: at })
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setShowScheduleBlock(false);
      setScheduleBlockInput("");
      setBlockErr(null);
    },
  });

  const setIsUzMutation = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      apiClient
        .patch<{ data: User }>(`/auth/users/${id}/`, { is_uz: value })
        .then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  // The switch is recorded as a dated entry in "Umowy główne" (backend
  // contract_timeline): which months owe a timesheet and which a B2B invoice is
  // read from that date, so the dialog asks for it.
  const setContractTypeMutation = useMutation({
    mutationFn: ({
      id,
      contract_type,
      contract_effective_from,
    }: {
      id: string;
      contract_type: string;
      contract_effective_from: string;
    }) =>
      apiClient
        .patch<{ data: User }>(`/auth/users/${id}/`, {
          contract_type,
          contract_effective_from,
        })
        .then((r) => r.data.data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["primary-contracts", id] });
    },
  });

  const setWorkTypeMutation = useMutation({
    mutationFn: ({
      id,
      work_type,
    }: {
      id: string;
      work_type: "on_site" | "remote";
    }) =>
      apiClient
        .patch<{ data: User }>(`/auth/users/${id}/`, { work_type })
        .then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const [editingFractionFor, setEditingFractionFor] = useState<string | null>(null);
  const [fractionNumerator, setFractionNumerator] = useState("1");
  const [fractionDenominator, setFractionDenominator] = useState("1");

  const setEmploymentFractionMutation = useMutation({
    mutationFn: ({ id, employment_fraction_numerator, employment_fraction_denominator }: {
      id: string; employment_fraction_numerator: number; employment_fraction_denominator: number;
    }) =>
      apiClient
        .patch<{ data: User }>(`/auth/users/${id}/`, { employment_fraction_numerator, employment_fraction_denominator })
        .then((r) => r.data.data),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["leave-entitlement", variables.id] });
      setEditingFractionFor(null);
    },
  });

  const [editingJobTitleFor, setEditingJobTitleFor] = useState<string | null>(
    null,
  );
  const [jobTitleInput, setJobTitleInput] = useState("");

  const setJobTitleMutation = useMutation({
    mutationFn: ({ id, job_title }: { id: string; job_title: string }) =>
      apiClient
        .patch<{ data: User }>(`/auth/users/${id}/`, { job_title })
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setEditingJobTitleFor(null);
    },
  });

  const [editingEntryCardFor, setEditingEntryCardFor] = useState<
    string | null
  >(null);
  const [entryCardInput, setEntryCardInput] = useState("");

  const setEntryCardMutation = useMutation({
    mutationFn: ({
      id,
      office_entry_card_number,
    }: {
      id: string;
      office_entry_card_number: string;
    }) =>
      apiClient
        .patch<{ data: User }>(`/auth/users/${id}/`, {
          office_entry_card_number: office_entry_card_number.trim(),
        })
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setEditingEntryCardFor(null);
    },
  });

  // ── Leave entitlement for selected employee ────────────────────────────────

  const currentYear = today.getFullYear();
  const [entitlementYear, setEntitlementYear] = useState(currentYear);
  const [entitlementForm, setEntitlementForm] = useState({
    days_total: "",
    prev_year_carryover: "",
  });
  const [editingEntitlement, setEditingEntitlement] = useState(false);
  // Mirrors LeaveEntitlementView.patch: director, CEO, accountant and HR.
  const canEditLeaveEntitlement =
    canManageUsers || authUser?.role === "accountant" || authUser?.role === "hr";

  const { data: empEntitlement, refetch: refetchEntitlement } = useQuery({
    queryKey: ["leave-entitlement", selectedEmployee, entitlementYear],
    queryFn: () =>
      apiClient
        .get<{
          data: LeaveEntitlement;
        }>(
          `/leave-entitlement/?employee=${selectedEmployee}&year=${entitlementYear}`,
        )
        .then((r) => r.data.data),
    enabled: !!selectedEmployee,
  });

  const saveEntitlementMutation = useMutation({
    mutationFn: (data: {
      employee: string;
      year: number;
      days_total?: number;
      prev_year_carryover?: number;
    }) =>
      apiClient
        .patch<{ data: LeaveEntitlement }>("/leave-entitlement/", data)
        .then((r) => r.data.data),
    onSuccess: () => {
      refetchEntitlement();
      setEditingEntitlement(false);
    },
  });

  // ── Allocation form ────────────────────────────────────────────────────────

  const [allocForm, setAllocForm] = useState({ project: "", percent: "100" });
  const [allocErr, setAllocErr] = useState<string | null>(null);
  const [allocExpanded, setAllocExpanded] = useState(false);
  const [allocViewMode, setAllocViewMode] = useState<"form" | "tiles">("form");
  const [tileSlots, setTileSlots] = useState<(string | null)[]>(Array(10).fill(null));
  const [selectedTileProject, setSelectedTileProject] = useState<string | null>(null);
  const [tileSaveErr, setTileSaveErr] = useState<string | null>(null);
  const [tileSaving, setTileSaving] = useState(false);
  const [tileSearch, setTileSearch] = useState("");
  const [tilePropagateToFuture, setTilePropagateToFuture] = useState(false);

  // ── Contract type change (dated) ──────────────────────────────────────────
  const [contractChangeModal, setContractChangeModal] = useState<{
    empId: string;
    empName: string;
    fromType: string;
    toType: "uop" | "uz" | "b2b";
    effectiveFrom: string;
    error: string | null;
  } | null>(null);

  // ── Branch change confirm ──────────────────────────────────────────────────
  const [branchChangeModal, setBranchChangeModal] = useState<{
    empId: string;
    empName: string;
    fromBranch: string | null;
    toBranchId: string | null;
    toBranchName: string;
  } | null>(null);

  // ── Proportional distribution exclusions ───────────────────────────────────

  const { data: exclusions = [] } = usePropDistExclusions(
    selectedEmployee
      ? { employee: selectedEmployee, year: allocYear, month: allocMonth }
      : null,
  );
  const createExclusion = useCreatePropDistExclusion();
  const deleteExclusion = useDeletePropDistExclusion();

  // Modal: ask about propagating exclusion change to future months
  const [propagateModal, setPropagateModal] = useState<{
    projectId: string;
    excluded: boolean; // new state after toggle
    exclusionId?: string; // id to delete when un-excluding
  } | null>(null);

  const handleExclusionToggle = (
    projectId: string,
    currentlyExcluded: boolean,
  ) => {
    if (!selectedEmployee) return;
    const exclusionId = exclusions.find((e) => e.project === projectId)?.id;
    // Apply the change for the current month immediately, then ask about future months
    if (currentlyExcluded && exclusionId) {
      deleteExclusion.mutate(exclusionId);
    } else if (!currentlyExcluded) {
      createExclusion.mutate({
        employee: selectedEmployee,
        project: projectId,
        year: allocYear,
        month: allocMonth,
      });
    }
    // Show propagation modal only if future months exist
    if (
      allocationMonths.some(
        (d) =>
          d.getFullYear() > allocYear ||
          (d.getFullYear() === allocYear && d.getMonth() + 1 > allocMonth),
      )
    ) {
      setPropagateModal({
        projectId,
        excluded: !currentlyExcluded,
        exclusionId,
      });
    }
  };

  const applyPropagateExclusion = async () => {
    if (!propagateModal || !selectedEmployee) return;
    const futureMonths = allocationMonths.filter((d) => {
      const y = d.getFullYear(),
        m = d.getMonth() + 1;
      return y > allocYear || (y === allocYear && m > allocMonth);
    });

    if (futureMonths.length > 0) {
      if (propagateModal.excluded) {
        // Add exclusion for each future month (skip duplicates)
        await propDistExclusionsApi.bulkCreate(
          futureMonths.map((d) => ({
            employee: selectedEmployee,
            project: propagateModal.projectId,
            year: d.getFullYear(),
            month: d.getMonth() + 1,
          })),
        );
      } else {
        // Remove exclusions for this project from next month onwards via single bulk-delete call
        const next = futureMonths[0];
        await propDistExclusionsApi.bulkDelete({
          employee: selectedEmployee,
          project: propagateModal.projectId,
          year: next.getFullYear(),
          month: next.getMonth() + 1,
        });
      }
      qc.invalidateQueries({ queryKey: ["prop-dist-exclusions"] });
      qc.refetchQueries({ queryKey: ["prop-dist-exclusions"] });
    }

    setPropagateModal(null);
  };

  const handleCreateAlloc = async () => {
    if (!selectedEmployee || !allocForm.project) {
      setAllocErr("Wybierz pracownika i projekt.");
      return;
    }
    const pct = parseFloat(allocForm.percent);
    if (isNaN(pct) || pct <= 0 || pct > 100) {
      setAllocErr("Procent etatu musi być 1–100.");
      return;
    }
    const currentTotal = allocations.reduce(
      (s, a) => s + parseFloat(a.fraction) * 100,
      0,
    );
    if (currentTotal + pct > 100.005) {
      setAllocErr(
        `Suma przekroczy 100% (już przypisano ${currentTotal.toFixed(1)}%, dodajesz ${pct}%).`,
      );
      return;
    }
    const fraction = (pct / 100).toFixed(3);
    try {
      await createAlloc.mutateAsync({
        employee: selectedEmployee,
        project: allocForm.project,
        year: allocYear,
        month: allocMonth,
        fraction,
      });

      if (propagateToFuture) {
        // Future months in the allowed range, after the currently selected month
        const futureMonths = allocationMonths.filter((d) => {
          const y = d.getFullYear(),
            m = d.getMonth() + 1;
          return y > allocYear || (y === allocYear && m > allocMonth);
        });
        for (const fd of futureMonths) {
          try {
            await createAlloc.mutateAsync({
              employee: selectedEmployee,
              project: allocForm.project,
              year: fd.getFullYear(),
              month: fd.getMonth() + 1,
              fraction,
            });
          } catch {
            // Silently skip — allocation already exists (unique_together)
          }
        }
      }

      setAllocForm({ project: "", percent: "100" });
      setAllocErr(null);
    } catch {
      setAllocErr("Błąd zapisu alokacji.");
    }
  };

  // ── Tile allocation sync & save ───────────────────────────────────────────

  useEffect(() => {
    const slots: (string | null)[] = Array(10).fill(null);
    let cursor = 0;
    for (const a of allocations) {
      const count = Math.round(parseFloat(a.fraction) * 10);
      for (let i = 0; i < count && cursor < 10; i++) slots[cursor++] = a.project;
    }
    setTileSlots(slots);
    setSelectedTileProject(null);
    setTileSaveErr(null);
  }, [allocations, allocYear, allocMonth, selectedEmployee]);

  // Assign color indices only to projects currently present in the tile bar (max 10 at once → no collisions)
  const tileColorIdx = useMemo(() => {
    const map: Record<string, number> = {};
    let ci = 0;
    for (const slot of tileSlots) {
      if (slot && !(slot in map)) map[slot] = ci++ % TILE_BORDER_CLASSES.length;
    }
    return map;
  }, [tileSlots]);

  const handleTileSave = async () => {
    if (!selectedEmployee) return;
    setTileSaving(true);
    setTileSaveErr(null);
    try {
      const counts: Record<string, number> = {};
      for (const slot of tileSlots) if (slot) counts[slot] = (counts[slot] ?? 0) + 1;

      // Apply full diff for the current month
      const existingByProject = new Map(allocations.map((a) => [a.project, a]));
      const ops: Promise<unknown>[] = [];
      for (const [pid, cnt] of Object.entries(counts)) {
        const frac = (cnt / 10).toFixed(3);
        const ex = existingByProject.get(pid);
        if (ex) {
          if (ex.fraction !== frac) ops.push(employeeAllocationsApi.update(ex.id, { fraction: frac }));
        } else {
          ops.push(employeeAllocationsApi.create({ employee: selectedEmployee, project: pid, year: allocYear, month: allocMonth, fraction: frac }));
        }
      }
      for (const a of allocations) if (!counts[a.project]) ops.push(employeeAllocationsApi.delete(a.id));
      await Promise.all(ops);

      // Propagate to future months: full replace (delete existing, create new)
      if (tilePropagateToFuture) {
        const futureMonths = allocationMonths.filter((d) => {
          const y = d.getFullYear(), m = d.getMonth() + 1;
          return y > allocYear || (y === allocYear && m > allocMonth);
        });
        for (const fd of futureMonths) {
          const fy = fd.getFullYear(), fm = fd.getMonth() + 1;
          const existing = await employeeAllocationsApi.list({
            employee: selectedEmployee,
            year: String(fy),
            month: String(fm),
          });
          await Promise.all(existing.map((a) => employeeAllocationsApi.delete(a.id)));
          await Promise.all(
            Object.entries(counts).map(([pid, cnt]) =>
              employeeAllocationsApi.create({
                employee: selectedEmployee,
                project: pid,
                year: fy,
                month: fm,
                fraction: (cnt / 10).toFixed(3),
              }),
            ),
          );
        }
      }

      qc.invalidateQueries({ queryKey: ["employee-allocations"] });
    } catch {
      setTileSaveErr("Błąd zapisu alokacji.");
    } finally {
      setTileSaving(false);
    }
  };

  // ── Leave requests for selected employee ──────────────────────────────────

  const { data: empLeaves = [] } = useQuery({
    queryKey: ["leave-requests", "employee", selectedEmployee],
    queryFn: () =>
      apiClient
        .get<{
          data: {
            id: string;
            leave_type: string;
            start_date: string;
            end_date: string;
            days_count: number;
            status: string;
            status_display: string;
            note: string;
            review_comment: string;
            reviewed_by_name: string | null;
            reviewed_at: string | null;
            created_at: string;
          }[];
        }>(`/leave-requests/?employee=${selectedEmployee}`)
        .then((r) => r.data.data),
    enabled: !!selectedEmployee,
  });

  const { data: empScheduleEntries = [] } = useQuery({
    queryKey: ["schedule-entries", "employee", selectedEmployee],
    queryFn: () =>
      apiClient
        .get<{
          data: {
            id: string;
            entry_type: string;
            entry_type_display: string;
            start_date: string;
            end_date: string;
            note: string;
            status: string;
            status_display: string;
            reviewed_by_name: string | null;
            review_comment: string;
            created_at: string;
          }[];
        }>(`/schedule/entries/?employee=${selectedEmployee}`)
        .then((r) => r.data.data),
    enabled: !!selectedEmployee,
  });

  const empResolvedRemote = empScheduleEntries.filter(
    (e) =>
      e.entry_type === "remote" &&
      (e.status === "approved" || e.status === "rejected"),
  );

  // ── Sick leave (L4) for selected employee ─────────────────────────────────

  const { data: empSickLeavesRes } = useQuery({
    queryKey: ["sick-leaves", "employee", selectedEmployee],
    queryFn: () => sickLeavesApi.list({ employee: selectedEmployee! }),
    enabled: !!selectedEmployee,
  });
  const empSickLeaves = empSickLeavesRes?.data ?? [];

  // ── Salary history for selected employee ──────────────────────────────────

  const empSalaries = salaryCosts
    .filter((s) => s.employee === selectedEmployee)
    .sort((a, b) => a.effective_from.localeCompare(b.effective_from));

  const getSalaryStatus = (
    s: EmployeeSalaryCost,
  ): "past" | "active" | "future" => {
    const effTo =
      s.effective_to && s.effective_to < "2099-01-01"
        ? s.effective_to
        : "2099-12-31";
    if (effTo < currentMonthStart) return "past";
    if (s.effective_from > currentMonthEnd) return "future";
    return "active";
  };

  const historicSalaries = empSalaries.filter(
    (s) => getSalaryStatus(s) !== "future",
  );

  // Compute projected salary for each of the next 3 months (highest-ID record wins on overlap)
  const salaryProjections = Array.from({ length: 3 }, (_, i) => {
    const fd = addMonths(today, i + 1);
    const fYear = fd.getFullYear();
    const fMonth = fd.getMonth() + 1;
    const mStart = `${fYear}-${String(fMonth).padStart(2, "0")}-01`;
    const lastDay = new Date(fYear, fMonth, 0).getDate();
    const mEnd = `${fYear}-${String(fMonth).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    const afterDismissal = selectedEmp?.planned_dismissal_date
      ? mStart > selectedEmp.planned_dismissal_date
      : false;
    const applicable = empSalaries.filter((s) => {
      const effTo =
        s.effective_to && s.effective_to < "2099-01-01"
          ? s.effective_to
          : "2099-12-31";
      return s.effective_from <= mEnd && effTo >= mStart;
    });
    const winner =
      applicable.length > 0
        ? applicable.reduce((best, s) => (s.id > best.id ? s : best))
        : null;
    return { year: fYear, month: fMonth, salary: winner, afterDismissal };
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isLoading) return <PageLoader />;

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 lg:h-full lg:min-h-0">
        {/* ═══════════════════════════════ LEFT PANEL ═════════════════════════ */}
        {/* Wide multi-column browse mode when nobody is selected; narrows to a
            single w-80 column once an employee is picked. */}
        <div
          className={`w-full flex flex-col gap-4 lg:min-h-0 lg:overflow-hidden ${
            selectedEmployee
              ? "lg:w-80 lg:flex-shrink-0"
              : "lg:flex-1 lg:min-w-0"
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">
                Pracownicy
              </h2>
              <p className="text-xs text-[var(--color-primary)] font-medium mt-0.5">
                {isCEO || authUser?.role === "hr" || authUser?.role === "accountant"
                  ? "Wszystkie oddziały"
                  : authUser?.branch?.name
                  ? `Oddział: ${authUser.branch.name}`
                  : null}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <SheetCalcToggleButton size="sm" />
              {canManageUsers && (
                <Button size="sm" onClick={() => setShowNewUser(!showNewUser)}>
                  {showNewUser ? "Anuluj" : "+ Dodaj"}
                </Button>
              )}
            </div>
          </div>

          {/* New user form */}
          {showNewUser && (
            <div className="bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] border border-[var(--color-primary)] rounded-lg p-3 flex flex-col gap-2">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                Nowe konto pracownika
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                  placeholder="Imię *"
                  value={newUserForm.first_name}
                  onChange={(e) =>
                    setNewUserForm({
                      ...newUserForm,
                      first_name: e.target.value,
                    })
                  }
                />
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                  placeholder="Nazwisko *"
                  value={newUserForm.last_name}
                  onChange={(e) =>
                    setNewUserForm({
                      ...newUserForm,
                      last_name: e.target.value,
                    })
                  }
                />
              </div>
              <input
                type="email"
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                placeholder="Email (login) *"
                value={newUserForm.email}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, email: e.target.value })
                }
              />
              <input
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                placeholder="Nr telefonu *"
                value={newUserForm.phone_number}
                onChange={(e) =>
                  setNewUserForm({
                    ...newUserForm,
                    phone_number: e.target.value,
                  })
                }
              />
              <select
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                value={newUserForm.role}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, role: e.target.value })
                }
              >
                {isCEO && <option value="director">Dyrektor</option>}
                <option value="coordinator">Koordynator</option>
                <option value="accountant">Księgowy</option>
                <option value="employee">Pracownik</option>
                <option value="hr">Kadrowa</option>
              </select>
              <select
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                value={newUserForm.branch}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, branch: e.target.value })
                }
              >
                <option value="">Wybierz oddział *</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <input
                type="password"
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                placeholder="Hasło *"
                value={newUserForm.password}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, password: e.target.value })
                }
              />
              <input
                type="password"
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                placeholder="Powtórz hasło *"
                value={newUserForm.password2}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, password2: e.target.value })
                }
              />
              {newUserErr && (
                <p className="text-xs text-red-600">{newUserErr}</p>
              )}
              <Button
                size="sm"
                loading={createUserMutation.isPending}
                onClick={() => {
                  if (!newUserForm.phone_number.trim()) {
                    setNewUserErr("Numer telefonu jest wymagany.");
                    return;
                  }
                  if (!newUserForm.branch) {
                    setNewUserErr("Oddział jest wymagany.");
                    return;
                  }
                  if (newUserForm.password !== newUserForm.password2) {
                    setNewUserErr("Hasła nie są identyczne.");
                    return;
                  }
                  if (newUserForm.password.length < 8) {
                    setNewUserErr("Hasło musi mieć min. 8 znaków.");
                    return;
                  }
                  setNewUserErr(null);
                  createUserMutation.mutate(newUserForm);
                }}
              >
                Utwórz konto
              </Button>
            </div>
          )}

          {/* Employee list */}
          <div
            className={`lg:overflow-y-auto lg:flex-1 lg:min-h-0 ${
              selectedEmployee
                ? "flex flex-col gap-1.5 lg:pr-0.5"
                : "flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60 bg-white dark:bg-gray-800"
            }`}
          >
            {!selectedEmployee && (
              <div
                className={`hidden md:grid ${EMPLOYEE_LIST_GRID} md:gap-x-6 sticky top-0 z-10 px-3 py-2 rounded-t-lg bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400`}
              >
                {(
                  [
                    ["name", "Pracownik", ""],
                    ["salary", "Płaca", "justify-self-end text-right"],
                    ["role", "Rola", ""],
                    ["email", "E-mail", ""],
                  ] as [EmployeeSortCol, string, string][]
                ).map(([col, label, cls]) => (
                  <button
                    key={col}
                    type="button"
                    onClick={() => toggleSort(col)}
                    className={`flex items-center gap-1 uppercase tracking-widest text-left hover:text-gray-700 dark:hover:text-gray-200 ${cls}`}
                  >
                    {label}
                    {sortCol === col && (
                      <span className="text-[var(--color-primary)]">
                        {sortDir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </button>
                ))}
                <span>Uwagi</span>
              </div>
            )}
            {internalUsers.map((u, idx) => {
              const prevUser = idx > 0 ? internalUsers[idx - 1] : null;
              const prevBranchId = prevUser ? prevUser.branch?.id : "__first__";
              const showBranchHeader =
                showBranchHeaders && u.is_active && u.branch?.id !== prevBranchId;
              const showInactiveHeader =
                !u.is_active && (!prevUser || prevUser.is_active);
              const sc = salaryCosts.filter((s) => s.employee === u.id);
              const isSelected = selectedEmployee === u.id;
              const today = new Date().toISOString().slice(0, 10);
              const activeScs = sc.filter((s) => {
                const effTo =
                  s.effective_to && s.effective_to < "2099-01-01"
                    ? s.effective_to
                    : "2099-12-31";
                return s.effective_from <= today && effTo >= today;
              });
              const activeTotalMonthly = activeScs.reduce(
                (sum, s) => sum + parseFloat(s.monthly_cost),
                0,
              );


              return (
                <Fragment key={u.id}>
                  {showBranchHeader && (
                    <div className={`px-3 py-1.5 bg-gray-100 dark:bg-gray-700/60 flex items-center gap-1.5 ${selectedEmployee ? "mt-1" : ""}`}>
                      <MapPin size={11} className="text-[var(--color-primary)] flex-shrink-0" />
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-600 dark:text-gray-300">
                        {u.branch?.name ?? "Bez oddziału"}
                      </span>
                    </div>
                  )}
                  {showInactiveHeader && (
                    <div className={`px-3 py-1.5 bg-gray-100 dark:bg-gray-700/60 flex items-center gap-1.5 ${selectedEmployee ? "mt-1" : ""}`}>
                      <UserX size={11} className="text-gray-400 flex-shrink-0" />
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                        Zablokowane konta
                      </span>
                    </div>
                  )}
                <div
                  className={`transition-colors bg-white dark:bg-gray-800 ${
                    isSelected ? "ring-1 ring-[var(--color-primary)]" : ""
                  } ${!u.is_active ? "opacity-60" : ""}`}
                >
                  {/* ── Clickable summary row ── */}
                  {(() => {
                    // Wide browse mode (nobody selected): single horizontal line
                    // per person so the list fits far more people vertically.
                    const wideList = !selectedEmployee;
                    const nameBlock = (
                      <div className="flex items-center gap-2 min-w-0">
                        <UserAvatar
                          userId={u.id}
                          fullName={`${u.first_name} ${u.last_name}`}
                          profilePictureUrl={u.profile_picture_url}
                          size="sm"
                        />
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate block">
                            {`${u.first_name} ${u.last_name}`.trim() ||
                              u.email || (
                                <span className="text-gray-400 dark:text-gray-500 italic font-normal">
                                  (brak danych)
                                </span>
                              )}
                          </span>
                          {/* Branch sub-label is redundant in the wide list when
                              rows are already grouped under branch headers. */}
                          {u.branch && !(wideList && showBranchHeaders) && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 block leading-none mt-0.5">{u.branch.name}</span>
                          )}
                        </div>
                      </div>
                    );
                    const roleBadge = (
                      <Badge color={getRoleBadgeColor(u.role, u.is_ceo)}>
                        {getRoleLabel(u.role, u.is_ceo)}
                      </Badge>
                    );
                    const owedFor = owedDaysOffByEmployee.get(u.id);
                    const extraBadges = (
                      <>
                        {!u.is_active && <Badge color="red">Zab.</Badge>}
                        {u.contract_type && u.contract_type !== "uop" && (
                          <Badge color="orange">
                            {CONTRACT_TYPE_LABELS[u.contract_type] ??
                              u.contract_type}
                          </Badge>
                        )}
                        {(u.contract_expiry_info ?? []).some(
                          (e) => e.days_to_expiry <= 45,
                        ) && (
                          <Badge color="red">
                            ⚠{" "}
                            {Math.min(
                              ...(u.contract_expiry_info ?? [])
                                .filter((e) => e.days_to_expiry <= 45)
                                .map((e) => e.days_to_expiry),
                            )}
                            d
                          </Badge>
                        )}
                        {/* Aggregated health/contract/signature/owed-day-off warning badges — visible only when backend returned data (dir/acc/hr). BHP/medical/contract are hidden for zarząd; esignature and the owed day off apply to everyone (including CEO). */}
                        {(u.bhp_status || u.medical_status || u.esignature_status || owedFor) &&
                          (() => {
                            const all = [
                              !u.is_ceo ? u.bhp_status : null,
                              !u.is_ceo ? u.medical_status : null,
                              !u.is_ceo ? u.contract_docs_status : null,
                              u.esignature_status,
                            ];
                            const red =
                              all.filter(
                                (s) =>
                                  s?.status === "expired" ||
                                  s?.status === "missing",
                              ).length + (owedFor?.length ?? 0);
                            const yellow = all.filter(
                              (s) => s?.status === "soon",
                            ).length;
                            if (red === 0 && yellow === 0) return null;
                            return (
                              <>
                                {red > 0 && (
                                  <Badge
                                    color="red"
                                    title={
                                      owedFor
                                        ? `W tym dzień wolny do oddania za: ${owedFor.map((d) => formatDate(d)).join(", ")}`
                                        : undefined
                                    }
                                  >
                                    {red}
                                  </Badge>
                                )}
                                {yellow > 0 && (
                                  <Badge color="yellow">{yellow}</Badge>
                                )}
                              </>
                            );
                          })()}
                      </>
                    );
                    const badgeCluster = (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {extraBadges}
                        {roleBadge}
                      </div>
                    );
                    const salaryHint =
                      activeScs.length > 0 ? (
                        <span
                          className="inline-block"
                          onClick={(e) => {
                            if (calcActive) e.stopPropagation();
                          }}
                        >
                          <CalcCell
                            id={`emp:${u.id}:0`}
                            tableId="emp"
                            index={idx}
                            col={0}
                            value={Number(activeTotalMonthly.toFixed(2))}
                            currency="PLN"
                          >
                            <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                              {formatAmount(activeTotalMonthly)} zł
                            </span>
                          </CalcCell>
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 italic">
                          Brak płacy
                        </span>
                      );
                    const warningHints = (
                      <>
                        {u.planned_dismissal_date && (
                          <span className="text-orange-500">
                            Zwolnienie: {formatDate(u.planned_dismissal_date)}
                          </span>
                        )}
                        {checkMissingTimesheets &&
                          u.is_uz &&
                          !notYetEmployedInPrevMonth(u) &&
                          !submittedEmployeeIds.has(u.id) &&
                          !dismissedTimesheetAlertIds.has(u.id) && (
                            <span className="text-red-500">
                              Brak karty pracy za {MONTH_NAMES[prevMonth]}
                            </span>
                          )}
                        {u.scheduled_deactivation_at && u.is_active && (
                          <span className="text-orange-500">
                            ⏰{" "}
                            {new Date(
                              u.scheduled_deactivation_at,
                            ).toLocaleDateString("pl-PL")}
                          </span>
                        )}
                        {!u.planned_dismissal_date &&
                          (u.contract_expiry_info ?? [])
                          .filter((e) => e.days_to_expiry <= 45)
                          .map((e, i) => (
                            <span key={i} className="text-red-500">
                              Kończąca się umowa za {e.days_to_expiry}{" "}
                              {e.days_to_expiry === 1 ? "dzień" : "dni"} (
                              {e.company_name})
                            </span>
                          ))}
                      </>
                    );
                    return (
                      <div
                        className={`px-3 cursor-pointer ${wideList ? "py-2" : "py-2.5"} ${isSelected ? "bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)]" : "hover:bg-gray-50 dark:hover:bg-gray-700/50"}`}
                        onClick={() =>
                          setSelectedEmployee(isSelected ? null : u.id)
                        }
                      >
                        {wideList ? (
                          /* One aligned table-like line per person:
                             name | email | warnings | badges | salary */
                          <div className={`flex flex-wrap items-center gap-x-4 gap-y-0.5 md:grid ${EMPLOYEE_LIST_GRID} md:gap-x-6`}>
                            <div className="min-w-0">{nameBlock}</div>
                            <span className="text-xs md:text-right">
                              {salaryHint}
                            </span>
                            <div>{roleBadge}</div>
                            <span className="hidden md:block truncate text-xs text-gray-400 dark:text-gray-500">
                              {u.email}
                            </span>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs min-w-0">
                              {extraBadges}
                              {warningHints}
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between gap-1">
                              {nameBlock}
                              {badgeCluster}
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                              {u.email}
                            </p>
                            {/* Compact status hints */}
                            <div className="flex items-center gap-3 mt-1 text-xs">
                              {salaryHint}
                              {warningHints}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── Expanded management — visible only when selected ── */}
                  {isSelected && canManageUsers && (
                    <div onClick={(e) => e.stopPropagation()}>
                      {/* — Planned dismissal — */}
                      <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2.5 bg-gray-50 dark:bg-gray-800/50">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                          Planowane zwolnienie
                        </p>
                        {editDismissalFor === u.id ? (
                          <div className="flex items-center gap-1.5">
                            <DatePicker
                              className="flex-1"
                              value={dismissalInput}
                              onChange={setDismissalInput}
                            />
                            <button
                              className="text-xs text-[var(--color-primary)] hover:underline font-medium"
                              onClick={() =>
                                setDismissalMutation.mutate({
                                  id: u.id,
                                  date: dismissalInput || null,
                                })
                              }
                            >
                              Zapisz
                            </button>
                            <button
                              className="text-xs text-gray-400 dark:text-gray-500 hover:underline"
                              onClick={() => setEditDismissalFor(null)}
                            >
                              Anuluj
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span
                              className={`text-xs ${u.planned_dismissal_date ? "text-orange-600 font-medium" : "text-gray-400 dark:text-gray-500"}`}
                            >
                              {formatDate(u.planned_dismissal_date, "Nie ustawiono")}
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                className="text-xs text-[var(--color-primary)] hover:underline"
                                onClick={() => {
                                  setEditDismissalFor(u.id);
                                  setDismissalInput(
                                    u.planned_dismissal_date ?? "",
                                  );
                                }}
                              >
                                {u.planned_dismissal_date ? "Zmień" : "Ustaw"}
                              </button>
                              {u.planned_dismissal_date && (
                                <button
                                  className="text-xs text-red-400 hover:underline"
                                  onClick={() =>
                                    setDismissalMutation.mutate({
                                      id: u.id,
                                      date: null,
                                    })
                                  }
                                >
                                  Usuń
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* — Job title — */}
                      <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2.5 bg-gray-50 dark:bg-gray-800/50">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                          Stanowisko
                        </p>
                        {editingJobTitleFor === u.id ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="text"
                              className="border border-gray-300 rounded px-2 py-1 text-xs flex-1"
                              value={jobTitleInput}
                              placeholder="np. Kierownik projektu"
                              autoFocus
                              onChange={(e) => setJobTitleInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  setJobTitleMutation.mutate({
                                    id: u.id,
                                    job_title: jobTitleInput,
                                  });
                                if (e.key === "Escape")
                                  setEditingJobTitleFor(null);
                              }}
                            />
                            <button
                              className="text-xs text-[var(--color-primary)] hover:underline font-medium"
                              onClick={() =>
                                setJobTitleMutation.mutate({
                                  id: u.id,
                                  job_title: jobTitleInput,
                                })
                              }
                            >
                              Zapisz
                            </button>
                            <button
                              className="text-xs text-gray-400 dark:text-gray-500 hover:underline"
                              onClick={() => setEditingJobTitleFor(null)}
                            >
                              Anuluj
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span
                              className={`text-xs ${u.job_title ? "text-gray-700 dark:text-gray-300" : "text-gray-400 dark:text-gray-500"}`}
                            >
                              {u.job_title || "Nie ustawiono"}
                            </span>
                            <button
                              className="text-xs text-[var(--color-primary)] hover:underline"
                              onClick={() => {
                                setEditingJobTitleFor(u.id);
                                setJobTitleInput(u.job_title ?? "");
                              }}
                            >
                              {u.job_title ? "Zmień" : "Ustaw"}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* — Employment type — */}
                      <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2.5 bg-gray-50 dark:bg-gray-800/50">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                          Typ zatrudnienia
                        </p>
                        <div className="flex items-center gap-2">
                          {(["uop", "uz", "b2b"] as const).map((ct) => (
                            <button
                              key={ct}
                              type="button"
                              disabled={setContractTypeMutation.isPending}
                              onClick={() => {
                                const current = u.contract_type ?? (u.is_uz ? "uz" : "uop");
                                if (current === ct) return;
                                setContractChangeModal({
                                  empId: u.id,
                                  empName: fullName(u),
                                  fromType: current,
                                  toType: ct,
                                  effectiveFrom: todayIso(),
                                  error: null,
                                });
                              }}
                              className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors disabled:opacity-50 ${
                                (u.contract_type ??
                                  (u.is_uz ? "uz" : "uop")) === ct
                                  ? "bg-[var(--color-primary)] border-[var(--color-primary)] text-white"
                                  : "border-gray-300 text-gray-600 dark:text-gray-400 hover:border-[var(--color-primary)]"
                              }`}
                            >
                              {ct.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* — Employment fraction (UoP only) — */}
                      {(u.contract_type ?? "uop") === "uop" && (
                        <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2.5 bg-gray-50 dark:bg-gray-800/50">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                            Wymiar etatu
                          </p>
                          {editingFractionFor === u.id ? (
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-1">
                                <input
                                  type="number" min={1}
                                  className="border border-gray-300 rounded px-2 py-1 text-xs w-14"
                                  value={fractionNumerator}
                                  onChange={(e) => setFractionNumerator(e.target.value)}
                                  autoFocus
                                />
                                <span className="text-xs text-gray-500">/</span>
                                <input
                                  type="number" min={1}
                                  className="border border-gray-300 rounded px-2 py-1 text-xs w-14"
                                  value={fractionDenominator}
                                  onChange={(e) => setFractionDenominator(e.target.value)}
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  className="text-xs text-[var(--color-primary)] hover:underline font-medium"
                                  onClick={() => {
                                    const num = parseInt(fractionNumerator, 10);
                                    const den = parseInt(fractionDenominator, 10);
                                    if (num >= 1 && den >= 1)
                                      setEmploymentFractionMutation.mutate({ id: u.id, employment_fraction_numerator: num, employment_fraction_denominator: den });
                                  }}
                                >
                                  Zapisz
                                </button>
                                <button
                                  className="text-xs text-gray-400 dark:text-gray-500 hover:underline"
                                  onClick={() => setEditingFractionFor(null)}
                                >
                                  Anuluj
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                                {u.employment_fraction_numerator ?? 1}/{u.employment_fraction_denominator ?? 1}
                              </span>
                              <button
                                className="text-xs text-[var(--color-primary)] hover:underline"
                                onClick={() => {
                                  setEditingFractionFor(u.id);
                                  setFractionNumerator(String(u.employment_fraction_numerator ?? 1));
                                  setFractionDenominator(String(u.employment_fraction_denominator ?? 1));
                                }}
                              >
                                Zmień
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* — Work type — */}
                      <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2.5 bg-gray-50 dark:bg-gray-800/50">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                          Typ pracy
                        </p>
                        <div className="flex items-center gap-2">
                          {(["on_site", "remote"] as const).map((wt) => (
                            <button
                              key={wt}
                              type="button"
                              disabled={setWorkTypeMutation.isPending}
                              onClick={() =>
                                setWorkTypeMutation.mutate({
                                  id: u.id,
                                  work_type: wt,
                                })
                              }
                              className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors disabled:opacity-50 ${
                                (u.work_type ?? "on_site") === wt
                                  ? "bg-[var(--color-primary)] border-[var(--color-primary)] text-white"
                                  : "border-gray-300 text-gray-600 dark:text-gray-400 hover:border-[var(--color-primary)]"
                              }`}
                            >
                              {wt === "on_site" ? "Stacjonarny" : "Zdalny"}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* — Salary costs — */}
                      {(isCEO || !isDirector || u.has_salary_view_access) && (
                      <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2.5">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                          Koszty płacy
                        </p>
                        {sc.length > 0 ? (
                          <div className="flex flex-col mb-2 divide-y divide-dashed divide-gray-200 dark:divide-gray-700">
                            {sc.map((s) => (
                              <div key={s.id} className="py-1.5 first:pt-0">
                                {editingSalaryId === s.id ? (
                                  <div className="rounded p-2 bg-white dark:bg-gray-800 flex flex-col gap-1.5 text-xs border border-[var(--color-primary)]">
                                    {isCEO && (
                                      <>
                                        <select
                                          className="w-full border border-gray-300 rounded px-2 py-1"
                                          value={editSalaryForm.company}
                                          onChange={(e) => setEditSalaryForm({ ...editSalaryForm, company: e.target.value })}
                                        >
                                          <option value="">— bez spółki —</option>
                                          {companies.map((c) => (
                                            <option key={c.id} value={c.id}>{c.short_code} — {c.name}</option>
                                          ))}
                                        </select>
                                        <input
                                          type="number"
                                          step="0.01"
                                          className="w-full border border-gray-300 rounded px-2 py-1"
                                          placeholder="Kwota PLN/mies. *"
                                          value={editSalaryForm.monthly_cost}
                                          onChange={(e) => setEditSalaryForm({ ...editSalaryForm, monthly_cost: e.target.value })}
                                        />
                                        <DatePicker
                                          placeholder="Obowiązuje od *"
                                          value={editSalaryForm.effective_from}
                                          onChange={(v) => setEditSalaryForm({ ...editSalaryForm, effective_from: v })}
                                        />
                                      </>
                                    )}
                                    <label className="flex items-center gap-1.5 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={editSalaryHasEndDate}
                                        onChange={(e) => setEditSalaryHasEndDate(e.target.checked)}
                                      />
                                      Ustaw datę zakończenia
                                    </label>
                                    {editSalaryHasEndDate && (
                                      <DatePicker
                                        value={editSalaryForm.effective_to}
                                        onChange={(v) => setEditSalaryForm({ ...editSalaryForm, effective_to: v })}
                                      />
                                    )}
                                    {editSalaryErr && <p className="text-red-500">{editSalaryErr}</p>}
                                    <div className="flex gap-1 pt-0.5">
                                      <Button size="sm" loading={updateSalary.isPending} onClick={handleUpdateSalary}>
                                        Zapisz
                                      </Button>
                                      <Button size="sm" variant="secondary" onClick={() => { setEditingSalaryId(null); setEditSalaryErr(null); }}>
                                        Anuluj
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex flex-col gap-0.5 text-xs">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="font-medium text-gray-800 dark:text-gray-200">{formatAmount(s.monthly_cost)} PLN</span>
                                      <span className="text-gray-400 dark:text-gray-500">
                                        {formatDate(s.effective_from)} — {!s.effective_to || s.effective_to >= "2099-01-01" ? "∞" : formatDate(s.effective_to)}
                                      </span>
                                      {s.company_name && (
                                        <span className="text-gray-400 dark:text-gray-500">· {s.company_name}</span>
                                      )}
                                      {s.is_primary && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                          Główny
                                        </span>
                                      )}
                                    </div>
                                    {(canManageUsers || isCEO) && (
                                      <div className="flex items-center gap-2 mt-0.5">
                                        {canManageUsers && !s.is_primary && (
                                          <button
                                            onClick={() => setPrimary.mutate(s.id)}
                                            disabled={setPrimary.isPending}
                                            className="text-emerald-600 hover:underline disabled:opacity-50"
                                          >
                                            Ustaw główny
                                          </button>
                                        )}
                                        {canManageUsers && (
                                          <button
                                            onClick={() => handleEditSalaryOpen(s)}
                                            className="text-[var(--color-primary)] hover:underline"
                                          >
                                            Edytuj
                                          </button>
                                        )}
                                        {isCEO && (
                                          <button
                                            onClick={() => { if (confirm("Usunąć koszt płacy?")) deleteSalary.mutate(s.id); }}
                                            className="text-red-400 hover:text-red-600"
                                          >
                                            Usuń
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                            Brak wpisów
                          </p>
                        )}

                        {showSalaryForm === u.id ? (
                          <div className="rounded p-2 bg-white dark:bg-gray-800 flex flex-col gap-1.5 text-xs">
                            {isCEO && (
                              <select
                                className="w-full border border-gray-300 rounded px-2 py-1"
                                value={salaryForm.company}
                                onChange={(e) =>
                                  setSalaryForm({
                                    ...salaryForm,
                                    company: e.target.value,
                                  })
                                }
                              >
                                <option value="">— bez spółki —</option>
                                {companies.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.short_code} — {c.name}
                                  </option>
                                ))}
                              </select>
                            )}
                            <input
                              type="number"
                              step="0.01"
                              className="w-full border border-gray-300 rounded px-2 py-1"
                              placeholder="Kwota PLN/mies. *"
                              value={salaryForm.monthly_cost}
                              onChange={(e) =>
                                setSalaryForm({
                                  ...salaryForm,
                                  monthly_cost: e.target.value,
                                })
                              }
                            />
                            <DatePicker
                              placeholder="Obowiązuje od *"
                              value={salaryForm.effective_from}
                              onChange={(v) =>
                                setSalaryForm({
                                  ...salaryForm,
                                  effective_from: v,
                                })
                              }
                            />
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={salaryHasEndDate}
                                onChange={(e) =>
                                  setSalaryHasEndDate(e.target.checked)
                                }
                              />
                              Ustaw datę końcową
                            </label>
                            {salaryHasEndDate && (
                              <DatePicker
                                value={salaryForm.effective_to}
                                onChange={(v) =>
                                  setSalaryForm({
                                    ...salaryForm,
                                    effective_to: v,
                                  })
                                }
                              />
                            )}
                            {salaryErr && (
                              <p className="text-red-500">{salaryErr}</p>
                            )}
                            <div className="flex gap-1 pt-0.5">
                              <Button
                                size="sm"
                                loading={createSalary.isPending}
                                onClick={() => handleCreateSalary(u.id)}
                              >
                                Zapisz
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  setShowSalaryForm(null);
                                  setSalaryErr(null);
                                }}
                              >
                                Anuluj
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <button
                            className="text-xs text-[var(--color-primary)] hover:underline"
                            onClick={() => {
                              setShowSalaryForm(u.id);
                              setSalaryForm({
                                monthly_cost: "",
                                effective_from: "",
                                effective_to: "",
                                company: "",
                              });
                              setSalaryHasEndDate(false);
                            }}
                          >
                            + Dodaj koszt płacy
                          </button>
                        )}
                      </div>
                      )}
                    </div>
                  )}
                </div>
                </Fragment>
              );
            })}
          </div>
        </div>

        {/* ═══════════════════════════════ RIGHT PANEL ════════════════════════ */}
        {/* Mirror of the left panel: narrow sidebar while browsing, takes over
            the screen once an employee is selected. */}
        <div
          ref={detailPanelRef}
          className={`flex flex-col gap-6 lg:overflow-y-auto lg:min-h-0 lg:pr-2 ${
            selectedEmployee
              ? "flex-1 min-w-0"
              : "lg:w-80 lg:flex-shrink-0"
          }`}
        >
          {canSeeAnalytics && (
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 text-left"
                onClick={() => setAnalyticsExpanded((v) => !v)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                    Analizy
                  </span>
                  <span className="text-xs text-[var(--color-text-tertiary)]">
                    eksport danych i raporty
                  </span>
                </div>
                <span className="text-xs text-[var(--color-primary)]">
                  {analyticsExpanded ? "▲ Zwiń" : "▼ Rozwiń"}
                </span>
              </button>
              {analyticsExpanded && (
                <div className="border-t border-[var(--color-border)] px-4 py-3 space-y-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-sm font-medium text-[var(--color-text-primary)]">
                        Ewidencja podpisów elektronicznych
                      </p>
                      <p className="text-xs text-[var(--color-text-tertiary)]">
                        {isDirector && !isCEO
                          ? "Pobierz arkusz XLSX z podpisami pracowników Twojego oddziału."
                          : "Pobierz arkusz XLSX z podpisami wszystkich pracowników."}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleExportESignatures}
                      loading={analyticsBusy}
                    >
                      Pobierz XLSX
                    </Button>
                  </div>
                  {canExportSalaryCosts && (
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="text-sm font-medium text-[var(--color-text-primary)]">
                          Koszty płac
                        </p>
                        <p className="text-xs text-[var(--color-text-tertiary)]">
                          Tabela XLSX: Imię i nazwisko | Koszt płacy | Oddział
                          — tylko pracownicy widoczni w tym widoku i objęci
                          Twoim dostępem do płac.
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={handleExportSalaryCosts}
                        loading={salaryExportBusy}
                      >
                        Pobierz XLSX
                      </Button>
                    </div>
                  )}
                  {analyticsError && (
                    <p className="text-xs text-red-600">{analyticsError}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {!selectedEmployee || !selectedEmp ? (
            <div className="flex items-center justify-center h-48 text-gray-400 dark:text-gray-500 text-sm">
              Wybierz pracownika po lewej, aby zarządzać alokacjami i historią
              płac
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <button
                type="button"
                className="self-start flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:underline"
                onClick={() => setSelectedEmployee(null)}
              >
                ← Wróć do listy pracowników
              </button>
              {/* — Header: name + status + block controls — */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <h3 className="font-bold text-gray-900 dark:text-gray-100 text-lg">
                      {selectedEmp.first_name} {selectedEmp.last_name}
                    </h3>
                    {!isCEO && selectedEmp.branch && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">{selectedEmp.branch.name}</span>
                    )}
                    <Badge
                      color={getRoleBadgeColor(
                        selectedEmp.role,
                        selectedEmp.is_ceo,
                      )}
                    >
                      {getRoleLabel(selectedEmp.role, selectedEmp.is_ceo)}
                    </Badge>
                    {!selectedEmp.is_active && (
                      <Badge color="red">Zablokowany</Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {selectedEmp.email}
                  </p>
                  {selectedEmp.job_title && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {selectedEmp.job_title}
                    </p>
                  )}
                  {selectedEmp.scheduled_deactivation_at &&
                    selectedEmp.is_active && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-xs text-orange-600">
                          ⏰ Zaplanowana blokada:{" "}
                          {formatDateTime(selectedEmp.scheduled_deactivation_at)}
                        </span>
                        <button
                          className="text-xs text-red-400 hover:underline"
                          onClick={() =>
                            scheduleBlockMutation.mutate({
                              id: selectedEmp.id,
                              at: null,
                            })
                          }
                        >
                          Anuluj blokadę
                        </button>
                      </div>
                    )}
                  {selectedEmp.planned_dismissal_date && (
                    <p className="text-xs text-orange-500 mt-0.5">
                      Planowane zwolnienie: {formatDate(selectedEmp.planned_dismissal_date)}
                    </p>
                  )}
                </div>

                {canManageUsers && (
                  <div className="flex flex-col sm:items-end gap-2 sm:flex-shrink-0">
                    {/* — Branch (CEO only) + office entry card, side by side — */}
                    <div className="flex items-center gap-3 flex-wrap sm:justify-end">
                      {isCEO && (
                        <select
                          value={selectedEmp.branch?.id ?? ""}
                          onChange={(e) => {
                            const newId = e.target.value || null;
                            const newName = branches.find((b) => b.id === newId)?.name ?? "bez oddziału";
                            setBranchChangeModal({
                              empId: selectedEmp.id,
                              empName: `${selectedEmp.first_name} ${selectedEmp.last_name}`,
                              fromBranch: selectedEmp.branch?.name ?? null,
                              toBranchId: newId,
                              toBranchName: newName,
                            });
                          }}
                          disabled={setBranchMutation.isPending}
                          className="font-mono text-xs border border-[var(--color-border-strong)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] px-2 py-0.5 focus:outline-none focus:border-[var(--color-primary)] disabled:opacity-50 cursor-pointer"
                        >
                          <option value="">— bez oddziału —</option>
                          {branches.map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                      )}
                      {/* — Office entry card (optional, HR-entered) — */}
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          Karta wejściowa:
                        </span>
                        {editingEntryCardFor === selectedEmp.id ? (
                          <>
                            <input
                              type="text"
                              className="font-mono text-xs border border-[var(--color-border-strong)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] px-2 py-0.5 w-32 focus:outline-none focus:border-[var(--color-primary)]"
                              value={entryCardInput}
                              placeholder="nr karty"
                              maxLength={50}
                              autoFocus
                              onChange={(e) => setEntryCardInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  setEntryCardMutation.mutate({
                                    id: selectedEmp.id,
                                    office_entry_card_number: entryCardInput,
                                  });
                                if (e.key === "Escape")
                                  setEditingEntryCardFor(null);
                              }}
                            />
                            <button
                              className="text-[var(--color-primary)] hover:underline font-medium disabled:opacity-50"
                              disabled={setEntryCardMutation.isPending}
                              onClick={() =>
                                setEntryCardMutation.mutate({
                                  id: selectedEmp.id,
                                  office_entry_card_number: entryCardInput,
                                })
                              }
                            >
                              Zapisz
                            </button>
                            <button
                              className="text-gray-400 dark:text-gray-500 hover:underline"
                              onClick={() => setEditingEntryCardFor(null)}
                            >
                              Anuluj
                            </button>
                          </>
                        ) : (
                          <>
                            <span
                              className={
                                selectedEmp.office_entry_card_number
                                  ? "font-mono text-gray-700 dark:text-gray-300"
                                  : "text-gray-400 dark:text-gray-500"
                              }
                            >
                              {selectedEmp.office_entry_card_number || "brak"}
                            </span>
                            <button
                              className="text-[var(--color-primary)] hover:underline"
                              onClick={() => {
                                setEditingEntryCardFor(selectedEmp.id);
                                setEntryCardInput(
                                  selectedEmp.office_entry_card_number ?? "",
                                );
                              }}
                            >
                              {selectedEmp.office_entry_card_number ? "Zmień" : "Ustaw"}
                            </button>
                            {selectedEmp.office_entry_card_number && (
                              <button
                                className="text-red-400 hover:underline disabled:opacity-50"
                                disabled={setEntryCardMutation.isPending}
                                onClick={() =>
                                  setEntryCardMutation.mutate({
                                    id: selectedEmp.id,
                                    office_entry_card_number: "",
                                  })
                                }
                              >
                                Usuń
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {selectedEmp.is_active ? (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={blockUserMutation.isPending}
                            onClick={() => {
                              if (
                                confirm(
                                  `Zablokować konto ${selectedEmp.first_name} ${selectedEmp.last_name}? Pracownik straci dostęp natychmiast.`,
                                )
                              ) {
                                blockUserMutation.mutate({
                                  id: selectedEmp.id,
                                  active: false,
                                });
                              }
                            }}
                          >
                            Zablokuj
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setShowScheduleBlock(!showScheduleBlock);
                              setBlockErr(null);
                            }}
                          >
                            Zablokuj z opóźnieniem
                          </Button>
                        </div>
                        {showScheduleBlock && (
                          <div className="flex items-center gap-2">
                            <input
                              type="datetime-local"
                              className="border border-gray-300 rounded px-2 py-1 text-xs"
                              value={scheduleBlockInput}
                              onChange={(e) =>
                                setScheduleBlockInput(e.target.value)
                              }
                            />
                            <Button
                              size="sm"
                              loading={scheduleBlockMutation.isPending}
                              onClick={() => {
                                if (!scheduleBlockInput) {
                                  setBlockErr("Wybierz datę i godzinę.");
                                  return;
                                }
                                scheduleBlockMutation.mutate({
                                  id: selectedEmp.id,
                                  at: scheduleBlockInput,
                                });
                              }}
                            >
                              Zaplanuj
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                setShowScheduleBlock(false);
                                setBlockErr(null);
                              }}
                            >
                              Anuluj
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={blockUserMutation.isPending}
                        onClick={() => {
                          if (
                            confirm(
                              `Przywrócić dostęp dla ${selectedEmp.first_name} ${selectedEmp.last_name}?`,
                            )
                          ) {
                            blockUserMutation.mutate({
                              id: selectedEmp.id,
                              active: true,
                            });
                          }
                        }}
                      >
                        Odblokuj
                      </Button>
                    )}
                    {blockErr && (
                      <p className="text-xs text-red-600">{blockErr}</p>
                    )}
                  </div>
                )}
              </div>

              {/* — Health / contract / signature issues summary — visible when backend returned data (dir/acc/hr). BHP/medical/contract suppressed for zarząd; esignature shown for everyone. */}
              {(() => {
                  const issues: {
                    label: string;
                    severity: "red" | "yellow";
                    onDismiss?: () => void;
                    onUndismiss?: () => void;
                  }[] = [];
                  const STATUS_LABEL: Record<string, string> = {
                    missing: "Brak daty",
                    expired: "Wygasło",
                    soon: "Wkrótce wygasa",
                  };
                  if (
                    !selectedEmp.bhp_status &&
                    !selectedEmp.medical_status &&
                    !selectedEmp.esignature_status &&
                    !owedDaysOffByEmployee.has(selectedEmp.id) &&
                    !(checkMissingTimesheets && selectedEmp.is_uz && !notYetEmployedInPrevMonth(selectedEmp) && !submittedEmployeeIds.has(selectedEmp.id))
                  ) return null;
                  if (
                    !selectedEmp.is_ceo &&
                    selectedEmp.bhp_status &&
                    selectedEmp.bhp_status.status !== "ok"
                  ) {
                    issues.push({
                      label: `Szkolenie BHP: ${STATUS_LABEL[selectedEmp.bhp_status.status]}${selectedEmp.bhp_status.valid_until ? ` (${new Date(selectedEmp.bhp_status.valid_until).toLocaleDateString("pl-PL")})` : ""}`,
                      severity:
                        selectedEmp.bhp_status.status === "soon"
                          ? "yellow"
                          : "red",
                    });
                  }
                  if (
                    !selectedEmp.is_ceo &&
                    selectedEmp.medical_status &&
                    selectedEmp.medical_status.status !== "ok"
                  ) {
                    issues.push({
                      label: `Medycyna pracy: ${STATUS_LABEL[selectedEmp.medical_status.status]}${selectedEmp.medical_status.valid_until ? ` (${new Date(selectedEmp.medical_status.valid_until).toLocaleDateString("pl-PL")})` : ""}`,
                      severity:
                        selectedEmp.medical_status.status === "soon"
                          ? "yellow"
                          : "red",
                    });
                  }
                  if (
                    !selectedEmp.is_ceo &&
                    selectedEmp.contract_docs_status &&
                    selectedEmp.contract_docs_status.status !== "ok"
                  ) {
                    issues.push({
                      label: `Umowa / aneks: ${STATUS_LABEL[selectedEmp.contract_docs_status.status]}${selectedEmp.contract_docs_status.valid_until ? ` (do ${new Date(selectedEmp.contract_docs_status.valid_until).toLocaleDateString("pl-PL")})` : ""}`,
                      severity:
                        selectedEmp.contract_docs_status.status === "soon"
                          ? "yellow"
                          : "red",
                    });
                  }
                  if (
                    selectedEmp.esignature_status &&
                    selectedEmp.esignature_status.status !== "ok"
                  ) {
                    issues.push({
                      label: `Podpis elektroniczny: ${STATUS_LABEL[selectedEmp.esignature_status.status]}${selectedEmp.esignature_status.valid_until ? ` (do ${new Date(selectedEmp.esignature_status.valid_until).toLocaleDateString("pl-PL")})` : ""}`,
                      severity:
                        selectedEmp.esignature_status.status === "soon"
                          ? "yellow"
                          : "red",
                    });
                  }
                  for (const owedDate of owedDaysOffByEmployee.get(selectedEmp.id) ?? []) {
                    issues.push({
                      label: `Dzień wolny do oddania za ${formatDate(owedDate)}`,
                      severity: "red",
                    });
                  }
                  if (
                    checkMissingTimesheets &&
                    selectedEmp.is_uz &&
                    !notYetEmployedInPrevMonth(selectedEmp) &&
                    !submittedEmployeeIds.has(selectedEmp.id) &&
                    !dismissedTimesheetAlertIds.has(selectedEmp.id)
                  ) {
                    issues.push({
                      label: `Brak karty pracy za ${MONTH_NAMES[prevMonth]}`,
                      severity: "red",
                      onDismiss: () => dismissTimesheetAlert.mutate(selectedEmp.id),
                    });
                  }
                  if (issues.length === 0) {
                    if (
                      checkMissingTimesheets &&
                      selectedEmp.is_uz &&
                      !notYetEmployedInPrevMonth(selectedEmp) &&
                      !submittedEmployeeIds.has(selectedEmp.id) &&
                      dismissedTimesheetAlertIds.has(selectedEmp.id) &&
                      recentTimesheetDismissalIds.has(selectedEmp.id)
                    ) {
                      return (
                        <>
                        <hr className="border-gray-200 dark:border-gray-700" />
                        <div className="rounded border-l-4 border-gray-300 bg-gray-50 dark:bg-gray-800/30 px-4 py-2 flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Brak karty pracy za {MONTH_NAMES[prevMonth]} — powiadomienie odrzucone
                          </span>
                          <button
                            onClick={() => undismissTimesheetAlert.mutate(selectedEmp.id)}
                            className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline shrink-0"
                          >
                            Przywróć
                          </button>
                        </div>
                        </>
                      );
                    }
                    return null;
                  }
                  const hasRed = issues.some((i) => i.severity === "red");
                  return (
                    <>
                    <hr className="border-gray-200 dark:border-gray-700" />
                    <div
                      className={`rounded border-l-4 px-4 py-3 space-y-1 ${hasRed ? "border-red-500 bg-red-50 dark:bg-red-950/30" : "border-amber-400 bg-amber-50 dark:bg-amber-950/30"}`}
                    >
                      <p
                        className={`text-xs font-semibold uppercase tracking-wide ${hasRed ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-400"}`}
                      >
                        {hasRed
                          ? "Wymaga pilnej uwagi"
                          : "Wkrótce wymaga uwagi"}
                      </p>
                      <ul className="space-y-0.5">
                        {issues.map((issue, i) => (
                          <li
                            key={i}
                            className={`text-sm flex items-center justify-between gap-1.5 ${issue.severity === "red" ? "text-red-800 dark:text-red-300" : "text-amber-800 dark:text-amber-300"}`}
                          >
                            <span className="flex items-center gap-1.5">
                              <span>{issue.severity === "red" ? "●" : "◐"}</span>
                              {issue.label}
                            </span>
                            {issue.onDismiss && (
                              <button
                                onClick={issue.onDismiss}
                                className="ml-2 text-xs opacity-60 hover:opacity-100 underline shrink-0"
                                title="Odrzuć powiadomienie"
                              >
                                Odrzuć
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                    </>
                  );
                })()}

              <hr className="border-gray-200 dark:border-gray-700" />

              {/* — Allocations section — */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-700 dark:text-gray-300">
                    Alokacje
                  </h4>
                  <button
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-md shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                    onClick={() => setAllocExpanded((v) => !v)}
                  >
                    {allocExpanded ? "▲ Zwiń" : "▼ Rozwiń"}
                  </button>
                </div>

                {allocExpanded && (
                  <>
                    {/* View mode toggle */}
                    <div className="flex items-center gap-0 mb-4 border-b border-gray-200 dark:border-gray-700">
                      {(["form", "tiles"] as const).map((mode) => (
                        <button
                          key={mode}
                          className={`px-4 py-1.5 text-xs font-semibold transition-colors rounded-t-md ${
                            allocViewMode === mode
                              ? "bg-white dark:bg-gray-800 border border-b-white dark:border-gray-600 dark:border-b-gray-800 text-gray-900 dark:text-gray-100 -mb-px"
                              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          }`}
                          onClick={() => {
                            setAllocViewMode(mode);
                            setSelectedTileProject(null);
                          }}
                        >
                          {mode === "form" ? "Formularz" : "Graficzny"}
                        </button>
                      ))}
                    </div>

                    {allocViewMode === "form" ? (
                    <>
                    {/* Add allocation form — at top */}
                    {isMonthInRange(allocYear, allocMonth) && (
                      <div className="bg-gray-50 dark:bg-gray-800/50 p-4 mb-4">
                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          + Dodaj alokację na {MONTHS[allocMonth - 1]}{" "}
                          {allocYear}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                              Przedsięwzięcie *
                            </label>
                            <select
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                              value={allocForm.project}
                              onChange={(e) =>
                                setAllocForm({
                                  ...allocForm,
                                  project: e.target.value,
                                })
                              }
                            >
                              <option value="">— wybierz —</option>
                              {activeProjects.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.short_name} — {p.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                              Wymiar etatu (%) *
                            </label>
                            <div className="relative">
                              <input
                                type="number"
                                step="1"
                                min="1"
                                max="100"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 pr-8 text-sm"
                                value={allocForm.percent}
                                onChange={(e) =>
                                  setAllocForm({
                                    ...allocForm,
                                    percent: e.target.value,
                                  })
                                }
                                placeholder="np. 50"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-400 dark:text-gray-500">
                                %
                              </span>
                            </div>
                          </div>
                          {allocationMonths.length > 1 && (
                            <div className="col-span-2">
                              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={propagateToFuture}
                                  onChange={(e) =>
                                    setPropagateToFuture(e.target.checked)
                                  }
                                />
                                Przekopiuj alokację na wszystkie następne
                                miesiące w oknie
                              </label>
                            </div>
                          )}
                          {allocErr && (
                            <p className="col-span-2 text-xs text-red-600">
                              {allocErr}
                            </p>
                          )}
                          <div className="col-span-2 flex gap-2">
                            <Button
                              size="sm"
                              loading={createAlloc.isPending}
                              onClick={handleCreateAlloc}
                            >
                              Dodaj alokację
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                setAllocForm({ project: "", percent: "100" });
                                setAllocErr(null);
                              }}
                            >
                              Wyczyść
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Month/year selectors */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Miesiąc:
                      </span>
                      <select
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                        value={allocMonth}
                        onChange={(e) =>
                          setAllocMonth(parseInt(e.target.value))
                        }
                      >
                        {allocationMonths.map((d) => (
                          <option
                            key={`${d.getFullYear()}-${d.getMonth()}`}
                            value={d.getMonth() + 1}
                          >
                            {MONTHS[d.getMonth()]} {d.getFullYear()}
                          </option>
                        ))}
                      </select>
                      <select
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                        value={allocYear}
                        onChange={(e) => setAllocYear(parseInt(e.target.value))}
                      >
                        {[
                          today.getFullYear() - 1,
                          today.getFullYear(),
                          today.getFullYear() + 1,
                        ].map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>

                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
                      Alokacje na {MONTHS[allocMonth - 1]} {allocYear}.
                      {!isMonthInRange(allocYear, allocMonth) && (
                        <span className="text-orange-500 ml-1">
                          ⚠ Poza oknem — edycja wyłączona.
                        </span>
                      )}
                    </p>

                    {allocations.length > 0 && (
                      <div className="bg-white dark:bg-gray-800 overflow-x-auto mb-4">
                        <table className="w-full text-xs min-w-[400px]">
                          <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
                            <tr>
                              <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                                Przedsięwzięcie
                              </th>
                              <th className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                                %
                              </th>
                              {isMonthInRange(allocYear, allocMonth) && (
                                <th className="px-3 py-2 w-16 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
                              )}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {allocations.map((a) => (
                              <tr key={a.id}>
                                <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                                  {a.project_name}
                                </td>
                                <td className="px-3 py-2 text-center font-medium">
                                  {Math.round(parseFloat(a.fraction) * 100)}%
                                </td>
                                {isMonthInRange(allocYear, allocMonth) && (
                                  <td className="px-3 py-2 text-right">
                                    <button
                                      onClick={() => {
                                        if (confirm("Usunąć tę alokację?"))
                                          deleteAlloc.mutate(a.id);
                                      }}
                                      className="text-xs text-red-500 hover:underline"
                                    >
                                      Usuń
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Unallocated distribution with checkboxes */}
                    {(() => {
                      const totalPct = allocations.reduce(
                        (s, a) => s + parseFloat(a.fraction) * 100,
                        0,
                      );
                      const remainingPct = 100 - totalPct;
                      if (Math.abs(remainingPct) < 0.05) return null;
                      const allocatedIds = new Set(
                        allocations.map((a) => a.project),
                      );
                      const unallocated = [
                        ...projects.filter(
                          (p) =>
                            p.status === "active" && !allocatedIds.has(p.id),
                        ),
                      ].sort((a, b) =>
                        (a.short_name ?? "").localeCompare(b.short_name ?? ""),
                      );
                      const excludedIds = new Set(
                        exclusions.map((e) => e.project),
                      );
                      const included = unallocated.filter(
                        (p) => !excludedIds.has(p.id),
                      );
                      const totalWeight = included.reduce(
                        (s, p) => s + (parseFloat(p.total_value ?? "0") || 1),
                        0,
                      );
                      const canEdit = isMonthInRange(allocYear, allocMonth);
                      const allChecked = unallocated.every(
                        (p) => !excludedIds.has(p.id),
                      );
                      const allUnchecked = unallocated.every((p) =>
                        excludedIds.has(p.id),
                      );
                      return (
                        <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-yellow-800">
                              Nieprzypisane: {remainingPct.toFixed(1)}% —
                              rozkład proporcjonalny na zaznaczone
                              przedsięwzięcia
                            </p>
                            {canEdit && unallocated.length > 0 && (
                              <div className="flex gap-2 shrink-0 ml-2">
                                <button
                                  className="text-xs text-yellow-700 hover:text-yellow-900 hover:underline disabled:opacity-40"
                                  disabled={allChecked}
                                  onClick={() =>
                                    unallocated
                                      .filter((p) => excludedIds.has(p.id))
                                      .forEach((p) =>
                                        handleExclusionToggle(p.id, true),
                                      )
                                  }
                                >
                                  Zaznacz wszystko
                                </button>
                                <button
                                  className="text-xs text-yellow-700 hover:text-yellow-900 hover:underline disabled:opacity-40"
                                  disabled={allUnchecked}
                                  onClick={() =>
                                    unallocated
                                      .filter((p) => !excludedIds.has(p.id))
                                      .forEach((p) =>
                                        handleExclusionToggle(p.id, false),
                                      )
                                  }
                                >
                                  Odznacz wszystko
                                </button>
                              </div>
                            )}
                          </div>
                          {unallocated.length === 0 ? (
                            <p className="text-xs text-yellow-700">
                              Brak aktywnych przedsięwzięć bez alokacji.
                            </p>
                          ) : (
                            <div className="flex flex-col gap-1 max-h-[150px] overflow-y-auto">
                              {unallocated.map((p) => {
                                const isExcluded = excludedIds.has(p.id);
                                const weight =
                                  parseFloat(p.total_value ?? "0") || 1;
                                const share =
                                  !isExcluded && totalWeight > 0
                                    ? (remainingPct * weight) / totalWeight
                                    : 0;
                                return (
                                  <label
                                    key={p.id}
                                    className="flex items-center justify-between text-xs text-yellow-900 cursor-pointer gap-2 hover:bg-yellow-100 rounded px-1 py-0.5"
                                  >
                                    <span className="flex items-center gap-1.5">
                                      <input
                                        type="checkbox"
                                        checked={!isExcluded}
                                        onChange={() =>
                                          isMonthInRange(
                                            allocYear,
                                            allocMonth,
                                          ) &&
                                          handleExclusionToggle(
                                            p.id,
                                            isExcluded,
                                          )
                                        }
                                        disabled={
                                          !isMonthInRange(allocYear, allocMonth)
                                        }
                                        className="accent-yellow-600 w-3.5 h-3.5"
                                      />
                                      <span
                                        className={
                                          isExcluded
                                            ? "line-through text-yellow-500"
                                            : ""
                                        }
                                      >
                                        {p.short_name} — {p.name}
                                      </span>
                                    </span>
                                    <span className="font-mono font-medium">
                                      {isExcluded
                                        ? "—"
                                        : `${share.toFixed(1)}%`}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    </> ) : (
                    /* ── Tiles view ── */
                    <>
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Miesiąc:</span>
                        <select
                          className="border border-gray-300 rounded px-2 py-1 text-sm"
                          value={allocMonth}
                          onChange={(e) => setAllocMonth(parseInt(e.target.value))}
                        >
                          {allocationMonths.map((d) => (
                            <option key={`${d.getFullYear()}-${d.getMonth()}`} value={d.getMonth() + 1}>
                              {MONTHS[d.getMonth()]} {d.getFullYear()}
                            </option>
                          ))}
                        </select>
                        <select
                          className="border border-gray-300 rounded px-2 py-1 text-sm"
                          value={allocYear}
                          onChange={(e) => setAllocYear(parseInt(e.target.value))}
                        >
                          {[today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1].map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>

                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        Alokacje na {MONTHS[allocMonth - 1]} {allocYear}.
                        {!isMonthInRange(allocYear, allocMonth) && (
                          <span className="text-orange-500 ml-1">⚠ Poza oknem — edycja wyłączona.</span>
                        )}
                      </p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500">
                        Zaokrągla do 10%. Wybierz projekt z listy → klikaj puste kafelki. Klik na zajęty kafelek = wybór projektu; ponowny klik = usunięcie.
                      </p>

                      {/* Tile bar */}
                      <div className="flex gap-1 mb-1">
                        {tileSlots.map((slot, idx) => {
                          const proj = slot ? activeProjects.find((p) => p.id === slot) : null;
                          const isActive = slot === selectedTileProject && slot !== null;
                          return (
                            <button
                              key={idx}
                              title={
                                proj
                                  ? `${proj.short_name} — ${proj.name}${proj.client_name ? ` (${proj.client_name})` : ""}`
                                  : "Nieprzypisane — wybierz projekt poniżej i kliknij"
                              }
                              disabled={!isMonthInRange(allocYear, allocMonth)}
                              className={`flex-1 h-12 rounded transition-all border-2 flex items-center justify-center overflow-hidden ${
                                isActive
                                  ? "bg-[var(--color-primary)] border-[var(--color-primary)] text-white"
                                  : slot !== null && slot in tileColorIdx
                                  ? TILE_BORDER_CLASSES[tileColorIdx[slot]]
                                  : "bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500"
                              } ${
                                isMonthInRange(allocYear, allocMonth)
                                  ? "cursor-pointer hover:brightness-110"
                                  : "cursor-default opacity-75"
                              }`}
                              onClick={() => {
                                if (!isMonthInRange(allocYear, allocMonth)) return;
                                if (slot !== null) {
                                  if (slot === selectedTileProject) {
                                    // Same project already selected → remove
                                    setTileSlots((prev) => { const n = [...prev]; n[idx] = null; return n; });
                                  } else {
                                    // Different project → pick it as paintbrush
                                    setSelectedTileProject(slot);
                                  }
                                } else if (selectedTileProject) {
                                  setTileSlots((prev) => { const n = [...prev]; n[idx] = selectedTileProject; return n; });
                                }
                              }}
                            >
                              <span className="text-[9px] font-bold leading-none text-center px-0.5 truncate w-full">
                                {proj ? proj.short_name.slice(0, 6) : "—"}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {/* Percentage scale */}
                      <div className="flex mb-4">
                        {Array.from({ length: 10 }, (_, i) => (
                          <span key={i} className="flex-1 text-center text-[10px] text-gray-400 dark:text-gray-500 font-mono">
                            {(i + 1) * 10}
                          </span>
                        ))}
                      </div>

                      {/* Project selector — searchable */}
                      <div className="mb-1">
                        <input
                          type="text"
                          placeholder="Szukaj projektu lub klienta…"
                          value={tileSearch}
                          onChange={(e) => setTileSearch(e.target.value)}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 mb-1"
                        />
                      </div>
                      <div className="flex flex-col gap-0 mb-4 max-h-56 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
                        {activeProjects
                          .filter((p) => {
                            if (!tileSearch.trim()) return true;
                            const q = tileSearch.toLowerCase();
                            return (
                              p.short_name.toLowerCase().includes(q) ||
                              p.name.toLowerCase().includes(q) ||
                              (p.client_name ?? "").toLowerCase().includes(q)
                            );
                          })
                          .map((p) => {
                            const count = tileSlots.filter((s) => s === p.id).length;
                            const isSelected = selectedTileProject === p.id;
                            return (
                              <button
                                key={p.id}
                                disabled={!isMonthInRange(allocYear, allocMonth)}
                                className={`flex items-center gap-2 text-xs px-2 py-1.5 text-left w-full border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors ${
                                  isSelected
                                    ? "bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)]"
                                    : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                } ${!isMonthInRange(allocYear, allocMonth) ? "opacity-60 cursor-default" : "cursor-pointer"}`}
                                onClick={() => {
                                  if (!isMonthInRange(allocYear, allocMonth)) return;
                                  setSelectedTileProject(p.id);
                                }}
                              >
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  {p.id in tileColorIdx ? (
                                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${TILE_DOT_CLASSES[tileColorIdx[p.id]]}`} />
                                  ) : (
                                    <span className="w-2.5 h-2.5 flex-shrink-0" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1">
                                    {isSelected && (
                                      <span className="text-[var(--color-primary)] font-bold text-[10px]">✎</span>
                                    )}
                                    <span className="font-medium text-gray-800 dark:text-gray-200 truncate">
                                      {p.short_name}
                                    </span>
                                    <span className="text-gray-400 dark:text-gray-500 truncate hidden sm:inline">
                                      — {p.name}
                                    </span>
                                  </div>
                                  {p.client_name && (
                                    <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{p.client_name}</div>
                                  )}
                                </div>
                                {count > 0 && (
                                  <span className={`font-mono font-semibold flex-shrink-0 text-[10px] ${p.id in tileColorIdx ? TILE_TEXT_CLASSES[tileColorIdx[p.id]] : "text-gray-500"}`}>
                                    {count * 10}%
                                  </span>
                                )}
                              </button>
                            );
                          })}
                      </div>

                      {/* Actions */}
                      {isMonthInRange(allocYear, allocMonth) && (
                        <div className="flex flex-col gap-2">
                          {allocationMonths.length > 1 && (
                            <label className="flex items-center gap-2 cursor-pointer w-fit">
                              <input
                                type="checkbox"
                                checked={tilePropagateToFuture}
                                onChange={(e) => setTilePropagateToFuture(e.target.checked)}
                                className="accent-[var(--color-primary)]"
                              />
                              <span className="text-xs text-gray-700 dark:text-gray-300">
                                Przekopiuj alokację na wszystkie następne miesiące w oknie
                              </span>
                            </label>
                          )}
                          <div className="flex gap-2 items-center flex-wrap">
                            <Button size="sm" loading={tileSaving} onClick={handleTileSave}>
                              Zapisz
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={tileSaving}
                              onClick={() => { setTileSlots(Array(10).fill(null)); setSelectedTileProject(null); setTileSaveErr(null); }}
                            >
                              Wyczyść
                            </Button>
                            {tileSaveErr && <p className="text-xs text-red-600">{tileSaveErr}</p>}
                          </div>
                        </div>
                      )}
                    </div>
                    </>
                    )}
                  </>
                )}
              </div>

              <hr className="border-gray-200 dark:border-gray-700" />

              {/* — Salary history table — */}
              {selectedEmp.has_salary_view_access !== false && (
              <div>
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Historia kosztów płacy
                </h4>
                {empSalaries.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Brak kosztów płacy dla tego pracownika.
                  </p>
                ) : (
                  <>
                    {/* Mobile cards */}
                    <div className="sm:hidden bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                      {historicSalaries.map((s) => {
                        const status = getSalaryStatus(s);
                        return (
                          <div
                            key={s.id}
                            className={`px-4 py-3 ${status === "past" ? "opacity-50" : ""}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-xs text-gray-700 dark:text-gray-300">
                                  {formatDate(s.effective_from)} —{" "}
                                  {!s.effective_to ||
                                  s.effective_to >= "2099-01-01"
                                    ? "∞"
                                    : formatDate(s.effective_to)}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {s.company_name ?? "—"}
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <span className="text-sm font-medium font-mono text-gray-900 dark:text-gray-100">
                                  {formatAmount(s.monthly_cost)}
                                </span>
                                {status === "past" ? (
                                  <Badge color="gray">Miniony</Badge>
                                ) : (
                                  <Badge color="green">Aktywny</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {salaryProjections.map(
                        ({ year, month, salary, afterDismissal }) => (
                          <div
                            key={`proj-${year}-${month}`}
                            className="px-4 py-3 bg-blue-50 dark:bg-blue-900/30"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                                {MONTHS[month - 1]} {year} (prognoza)
                              </p>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <span className="text-sm font-medium font-mono text-gray-900 dark:text-gray-100">
                                  {afterDismissal
                                    ? "—"
                                    : salary
                                      ? formatAmount(salary.monthly_cost)
                                      : "—"}
                                </span>
                                {afterDismissal ? (
                                  <Badge color="red">Po zwolnieniu</Badge>
                                ) : salary ? (
                                  <Badge color="blue">Przyszły</Badge>
                                ) : (
                                  <Badge color="gray">Brak danych</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                    {/* Desktop table */}
                    <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
                      <table className="w-full text-xs min-w-[400px]">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                          <tr>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Okres
                            </th>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Spółka
                            </th>
                            <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              PLN/mies.
                            </th>
                            <th className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {/* Historical & active records */}
                          {historicSalaries.map((s) => {
                            const status = getSalaryStatus(s);
                            return (
                              <tr
                                key={s.id}
                                className={
                                  status === "past" ? "opacity-50" : ""
                                }
                              >
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                  {formatDate(s.effective_from)} —{" "}
                                  {!s.effective_to ||
                                  s.effective_to >= "2099-01-01"
                                    ? "∞"
                                    : formatDate(s.effective_to)}
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                                  {isCEO ? (
                                    editingCompanyFor === s.id ? (
                                      <select
                                        autoFocus
                                        className="border border-[var(--color-primary)] rounded px-1 py-0.5 text-xs bg-white dark:bg-gray-700"
                                        defaultValue={s.company ?? ""}
                                        onBlur={() =>
                                          setEditingCompanyFor(null)
                                        }
                                        onChange={(e) => {
                                          updateSalary.mutate({
                                            id: s.id,
                                            data: {
                                              company: e.target.value || null,
                                            },
                                          });
                                          setEditingCompanyFor(null);
                                        }}
                                      >
                                        <option value="">— brak —</option>
                                        {companies.map((c) => (
                                          <option key={c.id} value={c.id}>
                                            {c.short_code}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <button
                                        onClick={() =>
                                          setEditingCompanyFor(s.id)
                                        }
                                        className="hover:underline cursor-pointer text-left"
                                        title="Kliknij aby zmienić spółkę"
                                      >
                                        {s.company_name ?? (
                                          <span className="text-gray-300 italic">
                                            — ustaw —
                                          </span>
                                        )}
                                      </button>
                                    )
                                  ) : (
                                    (s.company_name ?? (
                                      <span className="text-gray-300">—</span>
                                    ))
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                                  {formatAmount(s.monthly_cost)}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {status === "past" ? (
                                    <Badge color="gray">Miniony</Badge>
                                  ) : (
                                    <Badge color="green">Aktywny</Badge>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {/* Projections for next 3 months */}
                          {salaryProjections.map(
                            ({ year, month, salary, afterDismissal }) => (
                              <tr
                                key={`proj-${year}-${month}`}
                                className="bg-blue-50 dark:bg-blue-900/30"
                              >
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-400 italic">
                                  {MONTHS[month - 1]} {year} (prognoza)
                                </td>
                                <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                                  {afterDismissal
                                    ? "—"
                                    : salary
                                      ? formatAmount(salary.monthly_cost)
                                      : "—"}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {afterDismissal ? (
                                    <Badge color="red">Po zwolnieniu</Badge>
                                  ) : salary ? (
                                    <Badge color="blue">Przyszły</Badge>
                                  ) : (
                                    <Badge color="gray">Brak danych</Badge>
                                  )}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              )}

              <hr className="border-gray-200 dark:border-gray-700" />

              {/* — Main contract history (umowa główna) — */}
              {selectedEmp.has_salary_view_access !== false && (
                <>
                  <hr className="border-gray-200 dark:border-gray-700" />
                  <PrimaryEmploymentContractsPanel
                    employeeId={selectedEmp.id}
                    employeeName={
                      `${selectedEmp.first_name ?? ""} ${selectedEmp.last_name ?? ""}`.trim() ||
                      selectedEmp.email ||
                      ""
                    }
                    canManage={
                      isCEO ||
                      authUser?.role === "accountant" ||
                      (isDirector && !!selectedEmp.has_salary_view_access)
                    }
                    canManageScans={canManageContractScans}
                  />
                </>
              )}

              {/* — Secondary UZ contract (additional contract for UoP/B2B users) — */}
              {selectedEmp.has_salary_view_access !== false && (
                <>
                  <hr className="border-gray-200 dark:border-gray-700" />
                  <SecondaryEmploymentContractsPanel
                    employeeId={selectedEmp.id}
                    employeeName={
                      `${selectedEmp.first_name ?? ""} ${selectedEmp.last_name ?? ""}`.trim() ||
                      selectedEmp.email ||
                      ""
                    }
                    primaryContractType={
                      (selectedEmp.contract_type ||
                        (selectedEmp.is_uz ? "uz" : "uop")) as "uop" | "uz" | "b2b"
                    }
                    canManage={
                      isCEO ||
                      authUser?.role === "accountant" ||
                      (isDirector && !!selectedEmp.has_salary_view_access)
                    }
                    canManageScans={canManageContractScans}
                  />
                </>
              )}

              {/* — BHP / Medical examinations — UoP only — */}
              {(selectedEmp.contract_type ??
                (selectedEmp.is_uz ? "uz" : "uop")) === "uop" && (
                <>
                  <hr className="border-gray-200 dark:border-gray-700" />
                  <EmployeeExaminationPanel userId={selectedEmp.id} />
                </>
              )}

              {/* — Electronic signatures — all internal users — */}
              <hr className="border-gray-200 dark:border-gray-700" />
              <EmployeeESignaturePanel userId={selectedEmp.id} />

              {selectedEmp.has_salary_view_access !== false && (
                <>
                  <hr className="border-gray-200 dark:border-gray-700" />

                  {/* — Document repository — */}
                  <EmployeeDocumentsPanel
                    userId={selectedEmp.id}
                    canManageDocTypes={isDirector}
                    onOpenDocTypes={() => setShowDocTypes(true)}
                  />
                </>
              )}

              <hr className="border-gray-200 dark:border-gray-700" />

              {/* — Leave entitlement — UoP only */}
              {(selectedEmp.contract_type ??
                (selectedEmp.is_uz ? "uz" : "uop")) === "uop" && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300">
                      Wymiar urlopu
                    </h4>
                    <select
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                      value={entitlementYear}
                      onChange={(e) => {
                        setEntitlementYear(parseInt(e.target.value));
                        setEditingEntitlement(false);
                      }}
                    >
                      {[currentYear - 1, currentYear, currentYear + 1].map(
                        (y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ),
                      )}
                    </select>
                  </div>

                  {empEntitlement ? (
                    <div className="bg-white dark:bg-gray-800 p-4">
                      {!editingEntitlement ? (
                        <div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                            <div className="text-center">
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Przysługuje (pełny etat)
                              </p>
                              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                {empEntitlement.days_total}d
                              </p>
                              {empEntitlement.employment_fraction_numerator < empEntitlement.employment_fraction_denominator && (
                                <p className="text-xs text-[var(--color-primary)] mt-0.5">
                                  ×{empEntitlement.employment_fraction_numerator}/{empEntitlement.employment_fraction_denominator}
                                </p>
                              )}
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Z ubiegłego roku
                              </p>
                              <p className="text-xl font-bold text-[var(--color-primary)]">
                                +{empEntitlement.prev_year_carryover}d
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Wykorzystano
                              </p>
                              <p className="text-xl font-bold text-red-600">
                                {fmtEntitlementBalance(
                                  empEntitlement.used_days,
                                  empEntitlement.used_hours_remainder,
                                  empEntitlement.used_minutes_remainder,
                                )}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Pozostało
                              </p>
                              <p
                                className={`text-xl font-bold ${empEntitlement.available_days > 0 || empEntitlement.available_hours_remainder > 0 || empEntitlement.available_minutes_remainder > 0 ? "text-green-600" : "text-gray-400 dark:text-gray-500"}`}
                              >
                                {fmtEntitlementBalance(
                                  empEntitlement.available_days,
                                  empEntitlement.available_hours_remainder,
                                  empEntitlement.available_minutes_remainder,
                                )}
                              </p>
                            </div>
                          </div>
                          {canEditLeaveEntitlement && (
                            <button
                              className="text-xs text-[var(--color-primary)] hover:underline"
                              onClick={() => {
                                setEntitlementForm({
                                  days_total: String(empEntitlement.days_total),
                                  prev_year_carryover: String(
                                    empEntitlement.prev_year_carryover,
                                  ),
                                });
                                setEditingEntitlement(true);
                              }}
                            >
                              Edytuj wymiar
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                Przysługujące dni ({entitlementYear})
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="366"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                                value={entitlementForm.days_total}
                                onChange={(e) =>
                                  setEntitlementForm({
                                    ...entitlementForm,
                                    days_total: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                Przeniesione z {entitlementYear - 1}
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="366"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                                value={entitlementForm.prev_year_carryover}
                                onChange={(e) =>
                                  setEntitlementForm({
                                    ...entitlementForm,
                                    prev_year_carryover: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              className="text-xs bg-[var(--color-primary)] text-white px-3 py-1.5 rounded hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
                              disabled={saveEntitlementMutation.isPending}
                              onClick={() => {
                                if (!selectedEmployee) return;
                                saveEntitlementMutation.mutate({
                                  employee: selectedEmployee,
                                  year: entitlementYear,
                                  days_total: parseInt(
                                    entitlementForm.days_total,
                                  ),
                                  prev_year_carryover: parseInt(
                                    entitlementForm.prev_year_carryover,
                                  ),
                                });
                              }}
                            >
                              Zapisz
                            </button>
                            <button
                              className="text-xs text-gray-500 hover:underline"
                              onClick={() => setEditingEntitlement(false)}
                            >
                              Anuluj
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      Ładowanie...
                    </p>
                  )}
                </div>
              )}

              {/* — Days off in lieu of a holiday — UoP only */}
              {(selectedEmp.contract_type ??
                (selectedEmp.is_uz ? "uz" : "uop")) === "uop" && (
                <>
                  <hr className="border-gray-200 dark:border-gray-700" />
                  <EmployeeDaysOffPanel
                    employeeId={selectedEmp.id}
                    canManage={canManageDaysOff}
                  />
                </>
              )}

              <hr className="border-gray-200 dark:border-gray-700" />

              {/* — Leave requests history — */}
              <div>
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Historia urlopów
                </h4>
                {empLeaves.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Brak wniosków urlopowych.
                  </p>
                ) : (
                  <>
                    {/* Mobile cards */}
                    <div className="sm:hidden bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                      {[...empLeaves]
                        .sort((a, b) =>
                          b.start_date.localeCompare(a.start_date),
                        )
                        .map((lr) => (
                          <div
                            key={lr.id}
                            className={`px-4 py-3 ${lr.status === "rejected" ? "opacity-60" : ""}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                                  {LEAVE_TYPE_LABELS[
                                    lr.leave_type as keyof typeof LEAVE_TYPE_LABELS
                                  ] ?? lr.leave_type}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {formatDate(lr.start_date)} – {formatDate(lr.end_date)}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                  {new Date(lr.created_at).toLocaleDateString(
                                    "pl-PL",
                                  )}
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                                  {lr.days_count} dni
                                </span>
                                <Badge
                                  color={
                                    LEAVE_STATUS_COLORS[
                                      lr.status as keyof typeof LEAVE_STATUS_COLORS
                                    ] ?? "gray"
                                  }
                                >
                                  {lr.status_display}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                    {/* Desktop table */}
                    <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
                      <table className="w-full text-xs min-w-[400px]">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                          <tr>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Rodzaj
                            </th>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Okres
                            </th>
                            <th className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Dni
                            </th>
                            <th className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Status
                            </th>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Złożono
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {[...empLeaves]
                            .sort((a, b) =>
                              b.start_date.localeCompare(a.start_date),
                            )
                            .map((lr) => (
                              <tr
                                key={lr.id}
                                className={
                                  lr.status === "rejected" ? "opacity-60" : ""
                                }
                              >
                                <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                                  {LEAVE_TYPE_LABELS[
                                    lr.leave_type as keyof typeof LEAVE_TYPE_LABELS
                                  ] ?? lr.leave_type}
                                </td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                  {formatDate(lr.start_date)} – {formatDate(lr.end_date)}
                                </td>
                                <td className="px-3 py-2 text-center font-medium text-gray-900 dark:text-gray-100">
                                  {lr.days_count}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <Badge
                                    color={
                                      LEAVE_STATUS_COLORS[
                                        lr.status as keyof typeof LEAVE_STATUS_COLORS
                                      ] ?? "gray"
                                    }
                                  >
                                    {lr.status_display}
                                  </Badge>
                                </td>
                                <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
                                  {new Date(lr.created_at).toLocaleDateString(
                                    "pl-PL",
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              <hr className="border-gray-200 dark:border-gray-700" />

              {/* — L4 sick leave history — */}
              <div>
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Historia L4 (zwolnienia lekarskie)
                </h4>
                {empSickLeaves.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Brak zgłoszeń L4 dla tego pracownika.
                  </p>
                ) : (
                  <>
                    {/* Mobile cards */}
                    <div className="sm:hidden bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                      {[...empSickLeaves]
                        .sort((a, b) =>
                          b.start_date.localeCompare(a.start_date),
                        )
                        .map((sl) => (
                          <div
                            key={sl.id}
                            className={`px-4 py-3 ${sl.status === "rejected" ? "opacity-60" : ""}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-xs text-gray-700 dark:text-gray-300">
                                  {formatDate(sl.start_date)} – {formatDate(sl.end_date)}
                                </p>
                                {sl.note && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 italic mt-0.5">
                                    {sl.note}
                                  </p>
                                )}
                                {sl.verified_by_name && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                    Zweryfikował: {sl.verified_by_name}
                                    {sl.verification_comment && (
                                      <span className="italic ml-1">
                                        ({sl.verification_comment})
                                      </span>
                                    )}
                                  </p>
                                )}
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                  {new Date(sl.created_at).toLocaleDateString(
                                    "pl-PL",
                                  )}
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                                  {sl.days_count} dni
                                </span>
                                <Badge
                                  color={SICK_LEAVE_STATUS_COLORS[sl.status]}
                                >
                                  {sl.status_display}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                    {/* Desktop table */}
                    <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
                      <table className="w-full text-xs min-w-[400px]">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                          <tr>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Okres
                            </th>
                            <th className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Dni
                            </th>
                            <th className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Status
                            </th>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Zweryfikował
                            </th>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Złożono
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {[...empSickLeaves]
                            .sort((a, b) =>
                              b.start_date.localeCompare(a.start_date),
                            )
                            .map((sl) => (
                              <tr
                                key={sl.id}
                                className={
                                  sl.status === "rejected" ? "opacity-60" : ""
                                }
                              >
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                  {formatDate(sl.start_date)} – {formatDate(sl.end_date)}
                                  {sl.note && (
                                    <span className="ml-1 text-gray-400 dark:text-gray-500 italic">
                                      ({sl.note})
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center font-medium text-gray-900 dark:text-gray-100">
                                  {sl.days_count}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <Badge
                                    color={SICK_LEAVE_STATUS_COLORS[sl.status]}
                                  >
                                    {sl.status_display}
                                  </Badge>
                                </td>
                                <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">
                                  {sl.verified_by_name ?? "—"}
                                  {sl.verification_comment && (
                                    <span className="ml-1 italic text-gray-400 dark:text-gray-500">
                                      ({sl.verification_comment})
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
                                  {new Date(sl.created_at).toLocaleDateString(
                                    "pl-PL",
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              <hr className="border-gray-200 dark:border-gray-700" />

              {/* — Remote / UZ schedule entries history — */}
              <div>
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Historia wpisów harmonogramu
                </h4>
                {empScheduleEntries.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Brak wpisów (praca zdalna / obecności UZ).
                  </p>
                ) : (
                  <>
                    {/* Mobile cards */}
                    <div className="sm:hidden bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                      {[...empScheduleEntries]
                        .sort((a, b) =>
                          b.start_date.localeCompare(a.start_date),
                        )
                        .map((e) => (
                          <div
                            key={e.id}
                            className={`px-4 py-3 ${e.status === "rejected" ? "opacity-60" : ""}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                                  {SCHEDULE_TYPE_LABELS[e.entry_type] ??
                                    e.entry_type_display}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {formatDate(e.start_date)} – {formatDate(e.end_date)}
                                </p>
                                {e.reviewed_by_name && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                    Zatwierdził: {e.reviewed_by_name}
                                  </p>
                                )}
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                  {new Date(e.created_at).toLocaleDateString(
                                    "pl-PL",
                                  )}
                                </p>
                              </div>
                              <div className="shrink-0">
                                <Badge
                                  color={
                                    SCHEDULE_STATUS_COLORS[e.status] ?? "gray"
                                  }
                                >
                                  {e.status_display}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                    {/* Desktop table */}
                    <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
                      <table className="w-full text-xs min-w-[400px]">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                          <tr>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Typ
                            </th>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Okres
                            </th>
                            <th className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Status
                            </th>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Zatwierdził
                            </th>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                              Złożono
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {[...empScheduleEntries]
                            .sort((a, b) =>
                              b.start_date.localeCompare(a.start_date),
                            )
                            .map((e) => (
                              <tr
                                key={e.id}
                                className={
                                  e.status === "rejected" ? "opacity-60" : ""
                                }
                              >
                                <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                                  {SCHEDULE_TYPE_LABELS[e.entry_type] ??
                                    e.entry_type_display}
                                </td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                  {formatDate(e.start_date)} – {formatDate(e.end_date)}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <Badge
                                    color={
                                      SCHEDULE_STATUS_COLORS[e.status] ?? "gray"
                                    }
                                  >
                                    {e.status_display}
                                  </Badge>
                                </td>
                                <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">
                                  {e.reviewed_by_name ?? "—"}
                                </td>
                                <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
                                  {new Date(e.created_at).toLocaleDateString(
                                    "pl-PL",
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              <hr className="border-gray-200 dark:border-gray-700" />

              {/* — Resolved remote work requests — */}
              <div>
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Praca zdalna — wnioski
                </h4>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
                  Zatwierdzone i odrzucone wnioski o pracę zdalną.
                </p>
                <RemoteWorkResolvedTable
                  entries={empResolvedRemote}
                  showEmployee={false}
                />
              </div>

              {canManageUsers && (
                <>
                  {/* — Conversation — director/CEO only — */}
                  <div
                    ref={commentsRef}
                    className="scroll-mt-4 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/20 px-4 py-4"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <h4 className="font-semibold text-gray-700 dark:text-gray-300">
                        Notatki i konwersacja
                      </h4>
                      <span className="text-xs text-amber-600 dark:text-amber-500">
                        🔒 widoczne tylko dla Dyrektorów
                      </span>
                    </div>
                    <EmployeeCommentThread employeeId={selectedEmp.id} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Propagation modal */}
      <Modal
        open={!!propagateModal}
        onClose={() => setPropagateModal(null)}
        title={
          propagateModal?.excluded
            ? "Wykluczono przedsięwzięcie"
            : "Przywrócono przedsięwzięcie"
        }
      >
        <div className="flex flex-col gap-5">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {propagateModal?.excluded
              ? "Przedsięwzięcie zostało wykluczone z rozkładu proporcjonalnego dla wybranego miesiąca."
              : "Przedsięwzięcie zostało przywrócone do rozkładu proporcjonalnego dla wybranego miesiąca."}
            <br />
            <span className="font-medium">
              Czy zastosować ten stan również dla wszystkich następnych miesięcy
              w oknie?
            </span>
          </p>
          <div className="flex gap-3">
            <Button onClick={applyPropagateExclusion}>
              Tak, zastosuj dla przyszłych miesięcy
            </Button>
            <Button variant="secondary" onClick={() => setPropagateModal(null)}>
              Nie, tylko ten miesiąc
            </Button>
          </div>
        </div>
      </Modal>

      {showDocTypes && (
        <Modal
          open
          onClose={() => setShowDocTypes(false)}
          title="Typy dokumentów pracownika"
          size="lg"
        >
          <div className="max-h-[70vh] overflow-y-auto">
            <DocumentTypeManagerPage />
          </div>
        </Modal>
      )}

      <Modal
        open={!!contractChangeModal}
        onClose={() => setContractChangeModal(null)}
        title="Zmiana typu zatrudnienia"
        size="sm"
      >
        {contractChangeModal && (
          <div className="flex flex-col gap-5">
            <div className="border-l-2 border-[var(--color-primary)] pl-4">
              <p className="text-sm font-medium text-[var(--color-text-primary)]">
                {contractChangeModal.empName}
              </p>
              <div className="flex items-center gap-2 mt-2 text-xs text-[var(--color-text-muted)]">
                <span className="font-mono uppercase tracking-wider">
                  {CONTRACT_TYPE_LABELS[contractChangeModal.fromType] ?? contractChangeModal.fromType}
                </span>
                <span className="text-[var(--color-primary)]">→</span>
                <span className="font-mono uppercase tracking-wider font-bold text-[var(--color-text-primary)]">
                  {CONTRACT_TYPE_LABELS[contractChangeModal.toType]}
                </span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                Obowiązuje od *
              </label>
              <DatePicker
                value={contractChangeModal.effectiveFrom}
                onChange={(v) =>
                  setContractChangeModal({ ...contractChangeModal, effectiveFrom: v, error: null })
                }
              />
            </div>
            <p className="text-xs text-[var(--color-text-muted)]">
              Zmiana trafi do sekcji „Umowa główna — historia” — poprzednia umowa zakończy
              się dzień wcześniej. Od tej daty zależy, za które miesiące wymagamy karty godzin,
              a za które faktury B2B. W miesiącu zmiany wymagamy tylko dokumentu nowej umowy,
              ale pracownik może dodać oba.
            </p>
            {contractChangeModal.error && (
              <p className="text-xs text-red-600">{contractChangeModal.error}</p>
            )}
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setContractChangeModal(null)}
              >
                Anuluj
              </Button>
              <Button
                size="sm"
                disabled={!contractChangeModal.effectiveFrom}
                loading={setContractTypeMutation.isPending}
                onClick={() => {
                  setContractTypeMutation.mutate(
                    {
                      id: contractChangeModal.empId,
                      contract_type: contractChangeModal.toType,
                      contract_effective_from: contractChangeModal.effectiveFrom,
                    },
                    {
                      onSuccess: () => setContractChangeModal(null),
                      onError: (err) => {
                        const errors = (
                          err as { response?: { data?: { errors?: Record<string, unknown> } } }
                        )?.response?.data?.errors;
                        const msg = errors
                          ? Object.values(errors)
                              .map((m) => (Array.isArray(m) ? m.join(" ") : String(m)))
                              .join(" ")
                          : "";
                        setContractChangeModal((prev) =>
                          prev ? { ...prev, error: msg || "Nie udało się zmienić typu zatrudnienia." } : prev,
                        );
                      },
                    },
                  );
                }}
              >
                Potwierdź zmianę
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={!!branchChangeModal}
        onClose={() => setBranchChangeModal(null)}
        title="Zmiana oddziału"
        size="sm"
      >
        {branchChangeModal && (
          <div className="flex flex-col gap-5">
            <div className="border-l-2 border-[var(--color-primary)] pl-4">
              <p className="text-sm font-medium text-[var(--color-text-primary)]">
                {branchChangeModal.empName}
              </p>
              <div className="flex items-center gap-2 mt-2 text-xs text-[var(--color-text-muted)]">
                <span className="font-mono uppercase tracking-wider">
                  {branchChangeModal.fromBranch ?? "— bez oddziału —"}
                </span>
                <span className="text-[var(--color-primary)]">→</span>
                <span className="font-mono uppercase tracking-wider font-bold text-[var(--color-text-primary)]">
                  {branchChangeModal.toBranchId ? branchChangeModal.toBranchName : "— bez oddziału —"}
                </span>
              </div>
            </div>
            <p className="text-xs text-[var(--color-text-muted)]">
              Pracownik zostanie przypisany do nowego oddziału. Zmiana wpłynie na widoczność w widokach kadrowych i harmonogramie.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setBranchChangeModal(null)}
              >
                Anuluj
              </Button>
              <Button
                size="sm"
                loading={setBranchMutation.isPending}
                onClick={() => {
                  setBranchMutation.mutate(
                    { id: branchChangeModal.empId, branch: branchChangeModal.toBranchId },
                    { onSuccess: () => setBranchChangeModal(null) }
                  );
                }}
              >
                Potwierdź zmianę
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
